import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePaletteCommands } from '../context/CommandPaletteContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import logger from '../lib/logger';
import {
  Clock, Users, Calendar, BarChart3, Loader2, Settings, ArrowRight,
  TrendingUp, FileText, Download,
  FileType, Presentation, Save, FolderOpen, Trash2, Check, AlertCircle, BookOpen, HelpCircle, FileSpreadsheet,
  LayoutDashboard, GanttChart as GanttIcon, UserCog, FlaskConical, Camera, Briefcase, Shield,
  CalendarRange, CalendarDays, Target, ChevronUp, ChevronDown, Link2, Link2Off, MoreHorizontal, Mail, Lock, FileEdit, AlertTriangle, DollarSign, History, RotateCcw, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { getCurrencySymbol } from '../lib/currency';

import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { DatePickerField } from '../components/ui/date-picker-field';
import WhatIfSimulator from '../components/WhatIfSimulator';
import SnapshotManager from '../components/SnapshotManager';
import PageHelpButton from '../components/PageHelpButton';
import SectionGuide, { resetGuides } from '../components/SectionGuide';
import MathExplainer, { FormulaBlock, CalcStep, MathSection } from '../components/MathExplainer';
import EmailShareDialog from '../components/EmailShareDialog';
import { ROLE_COLORS } from '../components/planner/constants';
import { SummaryCard, DeltaBadge } from '../components/planner/SummaryCard';
import { MilestonesPanel } from '../components/planner/MilestonesPanel';
import { GanttChart } from '../components/planner/GanttChart';
import { buildProjectProgress } from '../components/planner/ganttHelpers';
import { WeeklyLoadingChart } from '../components/planner/WeeklyLoadingChart';
import { RoleAllocationPanel } from '../components/planner/RoleAllocationPanel';
import { PlannerNarrative } from '../components/planner/PlannerNarrative';
import { ClientResourcePanel } from '../components/planner/ClientResourcePanel';
import ResourceAssignmentsPanel from '../components/planner/ResourceAssignmentsPanel';
import { PlannerTemplateDialog } from '../components/planner/PlannerTemplateDialog';
import ReleaseConflictCallout from '../components/planner/ReleaseConflictCallout';
import { WavesPanel } from '../components/planner/WavesPanel';
import { SlipRollupCard } from '../components/planner/SlipRollupCard';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'timeline', label: 'Timeline', icon: GanttIcon },
  { id: 'resources', label: 'Resources', icon: UserCog },
  { id: 'waves', label: 'Waves', icon: Layers },
  { id: 'whatif', label: 'What-If', icon: FlaskConical },
];

export default function PlannerPage({ initialEstimateId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [estimates, setEstimates] = useState([]);
  const [selectedId, setSelectedId] = useState(searchParams.get('id') || initialEstimateId || '');
  const [compareId, setCompareId] = useState('');
  const [resourceDefaults, setResourceDefaults] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [compareTimeline, setCompareTimeline] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [ganttView, setGanttView] = useState('phase'); // 'phase' | 'group'
  // v261 — When Deployment Waves are in use, each wave carries its own
  // go-live & transition, so the overall phase timeline (which appends
  // Transition after the final go-live) is misleading. Default to the
  // waves view; the phase button is disabled while waves exist.
  const hasWaveSchedule = (timeline?.wave_schedule?.length || 0) > 0;
  useEffect(() => {
    if (hasWaveSchedule) setGanttView(v => (v === 'group' ? v : 'waves'));
    else setGanttView(v => (v === 'waves' ? 'phase' : v));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWaveSchedule, selectedId]);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [publicCloudReleases, setPublicCloudReleases] = useState([]);
  const [localFte, setLocalFte] = useState({});
  const [localHpd, setLocalHpd] = useState(8);
  const [localDpw, setLocalDpw] = useState(5);
  const [localUtil, setLocalUtil] = useState(80);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeExpanded, setNarrativeExpanded] = useState(true);
  const [projectWeeks, setProjectWeeks] = useState(null);
  const [durationMode, setDurationMode] = useState('preset');
  // ── Project Conditions (Batch C) ─────────────────────────────
  // When the selected estimate has `has_conditions=true`, the Planner
  // becomes a read-only derived view with break/blackout overlays.
  const currentEstimate = useMemo(
    () => estimates.find((e) => e.id === selectedId) || null,
    [estimates, selectedId],
  );
  const hasConditions = !!currentEstimate?.has_conditions;
  const projectConditions = currentEstimate?.project_conditions || null;

  // ── Conditions impact summary (Phase 3 — locked-notice inline preview) ──
  // When the Planner is locked behind Project Conditions, surface a tiny
  // recap so users see WHY the timeline moved without round-tripping back
  // to the Estimator. Two signals:
  //   • `extraWeeks` — total break weeks contributed by honored breaks
  //     (applies_to != 'none' AND, for 'specific', component_names non-empty)
  //   • `overlappingCount` — components in the rendered timeline whose
  //     [start_week, end_week] truly overlaps any honored break window.
  const conditionsImpact = useMemo(() => {
    if (!hasConditions || !projectConditions) {
      return { extraWeeks: 0, overlappingCount: 0, breakLabels: [] };
    }
    const breaks = projectConditions.breaks || [];
    const startIso = projectConditions.goal_start_date;
    let baseDate = null;
    if (startIso) {
      const d = new Date(startIso + 'T00:00:00');
      if (!isNaN(d.getTime())) baseDate = d;
    }
    const isoToWeek = (iso) => {
      if (!baseDate || !iso) return null;
      const d = new Date(iso + 'T00:00:00');
      if (isNaN(d.getTime())) return null;
      return Math.max(1, Math.floor((d - baseDate) / (1000 * 60 * 60 * 24) / 7) + 1);
    };
    const honored = []; // { id, label, applies, names, weeks, weekRange? }
    let extraWeeks = 0;
    for (const b of breaks) {
      const applies = (b.applies_to || 'all').toLowerCase();
      if (applies === 'none' || !b.start_date || !b.end_date) continue;
      const s = new Date(b.start_date + 'T00:00:00');
      const e = new Date(b.end_date + 'T00:00:00');
      if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) continue;
      const dayDelta = Math.floor((e - s) / (1000 * 60 * 60 * 24));
      const weeks = Math.max(0, Math.round(dayDelta / 7));
      if (applies === 'specific' && (!b.component_names || b.component_names.length === 0)) continue;
      const weekRange = baseDate ? { start_week: isoToWeek(b.start_date), end_week: isoToWeek(b.end_date) } : null;
      honored.push({ id: b.id, label: b.label || 'Break', applies, names: b.component_names || [], weeks, weekRange });
      extraWeeks += weeks;
    }
    // Walk every component on the rendered timeline and count those that
    // truly overlap a honored break window (week-level). Track the first
    // honored break we hit so the locked-notice chip can drill into it.
    const overlappingComponents = new Set();
    let firstOverlapBreakId = null;
    if (timeline?.group_schedule && honored.length) {
      for (const grp of timeline.group_schedule) {
        for (const comp of (grp.components || [])) {
          const cs = comp.start_week;
          const ce = comp.end_week;
          if (!cs || !ce) continue;
          for (const h of honored) {
            if (!h.weekRange) continue;
            if (h.applies === 'specific' && !h.names.includes(comp.name)) continue;
            if (h.weekRange.end_week >= cs && h.weekRange.start_week <= ce) {
              overlappingComponents.add(comp.name);
              if (!firstOverlapBreakId) firstOverlapBreakId = h.id;
              break;
            }
          }
        }
      }
    }
    return {
      extraWeeks,
      overlappingCount: overlappingComponents.size,
      breakLabels: honored.map(h => h.label).slice(0, 3),
      firstOverlapBreakId,
    };
  }, [hasConditions, projectConditions, timeline]);
  const [complexityPresets, setComplexityPresets] = useState({ simple: 10, standard: 20, complex: 48 });
  // Goal Dates mode
  const [goalStartDate, setGoalStartDate] = useState('');
  const [goalGoLiveDate, setGoalGoLiveDate] = useState('');
  // Absolute Dates mode
  const [phaseDates, setPhaseDates] = useState({});
  const [goalDatesLinked, setGoalDatesLinked] = useState(true);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [includeNarrative, setIncludeNarrative] = useState(true);
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [whatIfNarrative, setWhatIfNarrative] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [revisionNote, setRevisionNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedPlans, setSavedPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [savedPlansOpen, setSavedPlansOpen] = useState(false);
  const [savedBaseline, setSavedBaseline] = useState(null);
  // History panel state — separate from the Saved Plans menu so users can
  // open one revision history at a time without losing the plans list.
  const [historyPlanId, setHistoryPlanId] = useState(null);
  const [historyPlanName, setHistoryPlanName] = useState('');
  const [historyRevisions, setHistoryRevisions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [restoringRev, setRestoringRev] = useState(null);
  // Side-by-side diff: revision_numbers chosen as compare A / B.
  // Pre-seeded to (current, prior) so the most useful diff is visible
  // immediately when the dialog opens, but users can pick any pair.
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const [guideResetKey, setGuideResetKey] = useState(0);
  const tabContentRef = useRef(null);
  const [snapshotting, setSnapshotting] = useState(false);
  const [templateBrowseOpen, setTemplateBrowseOpen] = useState(false);
  const [templateSaveOpen, setTemplateSaveOpen] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';

  const handleApplyTemplate = useCallback((tpl) => {
    if (tpl.resource_overrides) setLocalFte(tpl.resource_overrides);
    if (tpl.hours_per_day) setLocalHpd(tpl.hours_per_day);
    if (tpl.days_per_week) setLocalDpw(tpl.days_per_week);
    if (tpl.utilization_pct) setLocalUtil(tpl.utilization_pct);
    if (tpl.project_weeks) {
      setProjectWeeks(tpl.project_weeks);
      setDurationMode('manual');
    }
    if (tpl.complexity_week_presets) setComplexityPresets(tpl.complexity_week_presets);
    // Invalidate current timeline so user regenerates with new settings
    setTimeline(null);
    setNarrative(null);
  }, []);

  const currentTemplateSettings = {
    resource_overrides: localFte,
    hours_per_day: localHpd,
    days_per_week: localDpw,
    utilization_pct: localUtil,
    project_weeks: projectWeeks,
    complexity_week_presets: complexityPresets,
    estimate_id: selectedId || null,
    estimate_total_effort: timeline?.total_effort_hours || null,
    estimate_total_cost: estimates.find(e => e.id === selectedId)?.results?.total_cost || null,
  };

  const generateSnapshotPdf = useCallback(async () => {
    const el = tabContentRef.current;
    if (!el) return null;
    const tabLabel = TABS.find(t => t.id === activeTab)?.label || activeTab;
    const est = estimates.find(e => e.id === selectedId);
    const estName = est?.estimate_name || 'Untitled Estimate';
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

    const canvas = await html2canvas(el, {
      scale: 2, useCORS: true, backgroundColor: '#FFFFFF', logging: false,
      windowWidth: el.scrollWidth, windowHeight: el.scrollHeight,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    const imgW = canvas.width;
    const imgH = canvas.height;
    const margin = 12;
    const headerH = 18;
    const footerH = 8;
    const orientation = imgH > imgW * 0.8 ? 'p' : 'l';

    const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();
    const usableW = pw - margin * 2;
    const usableH = ph - margin * 2 - headerH - footerH;

    pdf.setFillColor(15, 32, 64);
    pdf.rect(0, 0, pw, headerH + margin, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Seertech Solutions', margin, margin + 6);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Seertech Blueprint Studio', margin, margin + 12);
    pdf.setFontSize(9);
    pdf.text(`${dateStr}  ${timeStr}`, pw - margin, margin + 6, { align: 'right' });
    pdf.setFontSize(8);
    pdf.text(estName, pw - margin, margin + 12, { align: 'right' });

    const bodyTop = headerH + margin + 6;
    pdf.setTextColor(15, 32, 64);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Project Planner — ${tabLabel}`, margin, bodyTop);
    pdf.setDrawColor(74, 191, 207);
    pdf.setLineWidth(0.6);
    pdf.line(margin, bodyTop + 2, margin + 50, bodyTop + 2);

    const imgTop = bodyTop + 6;
    const availH = usableH - (imgTop - (headerH + margin));
    const scale = Math.min(usableW / imgW, availH / imgH);
    pdf.addImage(imgData, 'JPEG', margin, imgTop, imgW * scale, imgH * scale);

    pdf.setFillColor(245, 247, 250);
    pdf.rect(0, ph - footerH - 4, pw, footerH + 4, 'F');
    pdf.setFontSize(7);
    pdf.setTextColor(148, 163, 184);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Generated by Seertech Blueprint Studio — Seertech Solutions', margin, ph - 4);
    pdf.text('Confidential', pw - margin, ph - 4, { align: 'right' });

    return { pdf, tabLabel, estName, now };
  }, [activeTab, selectedId, estimates]);

  const captureSnapshot = useCallback(async () => {
    setSnapshotting(true);
    try {
      const result = await generateSnapshotPdf();
      if (!result) return;
      const { pdf, tabLabel, estName, now } = result;
      const safeName = estName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      pdf.save(`Planner_${tabLabel}_${safeName}_${now.toISOString().slice(0, 10)}.pdf`);
      toast.success(`${tabLabel} snapshot exported as PDF`);
    } catch (err) {
      logger.error('Snapshot error:', err);
      toast.error('Failed to generate snapshot');
    } finally {
      setSnapshotting(false);
    }
  }, [generateSnapshotPdf]);

  const generateSnapshotBase64 = useCallback(async () => {
    try {
      const result = await generateSnapshotPdf();
      if (!result) return null;
      const arrayBuffer = result.pdf.output('arraybuffer');
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return btoa(binary);
    } catch (err) {
      logger.error('PDF generation error:', err);
      return null;
    }
  }, [generateSnapshotPdf]);

  // Sync tab to URL
  const switchTab = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    setSearchParams(params, { replace: true });
  };

  // ── Register ⌘K palette commands for the Planner page ─────────
  // Tab-switching + estimate-loading commands. Available app-wide via
  // the global palette so users can jump straight to a specific
  // estimate's planner view without going through the dropdown.
  usePaletteCommands(
    'planner-actions',
    () => {
      const items = TABS.map((t) => ({
        id: `planner-tab-${t.id}`,
        value: `Switch to ${t.label} planner tab`,
        icon: t.icon,
        label: `Planner — ${t.label}`,
        onSelect: () => switchTab(t.id),
      }));
      // Top-6 estimates as quick-load actions
      estimates.slice(0, 6).forEach((e) => {
        if (e.id === selectedId) return;
        const label = e.estimate_name || e.organization_name || 'Untitled';
        items.push({
          id: `planner-load-${e.id}`,
          value: `Load planner for ${e.estimate_name || ''} ${e.organization_name || ''} ${e.id}`,
          icon: FolderOpen,
          label: `Load planner: ${label}`,
          onSelect: () => setSelectedId(e.id),
        });
      });
      return items.length > 0 ? { heading: 'Planner', priority: 5, items } : null;
    },
    { deps: [estimates, selectedId, searchParams] }
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [estRes, resRes, relRes] = await Promise.all([
          axios.get(`${API_URL}/api/estimates`, { withCredentials: true }),
          axios.get(`${API_URL}/api/planner/resources`, { withCredentials: true }),
          axios.get(`${API_URL}/api/settings/public-cloud-releases`, { withCredentials: true })
            .catch(() => ({ data: { events: [] } })),
        ]);
        setEstimates(estRes.data.estimates || []);
        const rd = resRes.data;
        setResourceDefaults(rd);
        setLocalFte(rd.fte_by_role || {});
        setLocalHpd(rd.hours_per_day || 8);
        setLocalDpw(rd.days_per_week || 5);
        setLocalUtil(rd.utilization_pct || 80);
        if (rd.complexity_week_presets) setComplexityPresets(rd.complexity_week_presets);
        setPublicCloudReleases(relRes.data?.events || []);
      } catch { /* ignore */ }
    };
    load();
  }, []);

  useEffect(() => {
    if (selectedId && resourceDefaults) {
      generateTimeline(selectedId);
      // Pre-populate Goal Start Date from estimate.preferred_start_date
      // when the user hasn't set one yet in this planner session. Keeps
      // Estimator ↔ Planner in sync (two-way binding).
      const est = estimates.find(e => e.id === selectedId);
      if (est?.preferred_start_date && !goalStartDate) {
        setGoalStartDate(est.preferred_start_date);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, resourceDefaults]);

  const generateTimeline = useCallback(async (estId, { silent = false } = {}) => {
    if (!estId) return;
    if (!silent) {
      // Silent mode keeps the GanttChart mounted so expanded waves/groups stay open.
      setLoading(true);
      setTimeline(null);
      setCompareTimeline(null);
      setComparison(null);
      setNarrative(null);
    }
    try {
      const payload = {
        estimate_id: estId,
        resource_overrides: localFte,
        hours_per_day: localHpd,
        days_per_week: localDpw,
        utilization_pct: localUtil,
      };
      if (durationMode === 'preset' || durationMode === 'manual') {
        payload.plan_mode = 'duration';
        if (projectWeeks) payload.project_weeks = projectWeeks;
      } else if (durationMode === 'goals') {
        payload.plan_mode = 'goals';
        payload.goal_start_date = goalStartDate;
        payload.goal_golive_date = goalGoLiveDate;
      } else if (durationMode === 'absolute') {
        payload.plan_mode = 'absolute';
        payload.phase_dates = phaseDates;
        if (projectWeeks) payload.project_weeks = projectWeeks;
      }
      const res = await axios.post(`${API_URL}/api/planner/timeline`, payload, { withCredentials: true });
      setTimeline(res.data);
      // Warn if any user-overridden component had its Weeks clamped to fit the project horizon
      const truncated = [];
      (res.data?.group_schedule || []).forEach(g => {
        (g?.components || []).forEach(c => {
          if (c?.planner_weeks_truncated_from) {
            const actual = (c.end_week || 0) - (c.start_week || 0) + 1;
            truncated.push({ group: g.group, name: c.name, from: c.planner_weeks_truncated_from, to: actual });
          }
        });
      });
      if (truncated.length) {
        const first = truncated[0];
        const suffix = truncated.length > 1 ? ` (+${truncated.length - 1} more)` : '';
        toast.warning(
          `${first.group} — ${first.name}: duration trimmed from ${first.from} to ${first.to} week${first.to === 1 ? '' : 's'} to fit project horizon${suffix}.`,
          { duration: 6000 }
        );
      }
    } catch {
      toast.error('Failed to generate timeline');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [localFte, localHpd, localDpw, localUtil, projectWeeks, durationMode, goalStartDate, goalGoLiveDate, phaseDates]);

  const generateComparison = useCallback(async () => {
    if (!selectedId || !compareId) return;
    setLoading(true);
    try {
      const payload = {
        estimate_ids: [selectedId, compareId],
        resource_overrides: localFte,
        hours_per_day: localHpd,
        days_per_week: localDpw,
        utilization_pct: localUtil,
      };
      if (projectWeeks) payload.project_weeks = projectWeeks;
      const res = await axios.post(`${API_URL}/api/planner/compare`, payload, { withCredentials: true });
      setTimeline(res.data.timelines[0]);
      setCompareTimeline(res.data.timelines[1]);
      setComparison(res.data.comparison);
    } catch {
      toast.error('Failed to generate comparison');
    } finally {
      setLoading(false);
    }
  }, [selectedId, compareId, localFte, localHpd, localDpw, localUtil, projectWeeks]);

  const generateNarrative = useCallback(async () => {
    if (!timeline) return;
    setNarrativeLoading(true);
    setNarrative(null);
    try {
      const est = estimates.find(e => e.id === selectedId);
      const res = await axios.post(`${API_URL}/api/planner/narrative`, {
        timeline_data: timeline,
        estimate_name: est?.estimate_name || timeline.estimate_name || '',
        implementation_type: est?.implementation_type || '',
        complexity: est?.complexity || '',
      }, { withCredentials: true });
      setNarrative(res.data.narrative);
      setNarrativeExpanded(true);
    } catch {
      toast.error('Failed to generate narrative');
    } finally {
      setNarrativeLoading(false);
    }
  }, [timeline, selectedId, estimates]);

  const LAST_EXPORT_PREFIX = 'plannerLastExport:';
  const [lastExportInfo, setLastExportInfo] = useState(null); // {format, filename, timestamp}

  const readLastExport = useCallback((estimateId) => {
    if (!estimateId) { setLastExportInfo(null); return; }
    // Prefer server-side record (persisted per estimate, visible to all collaborators),
    // fall back to the per-browser localStorage record so solo users aren't blocked.
    const est = estimates.find(e => e.id === estimateId);
    const serverRecord = est?.last_planner_export;
    if (serverRecord && serverRecord.timestamp) {
      setLastExportInfo(serverRecord);
      return;
    }
    try {
      const raw = localStorage.getItem(`${LAST_EXPORT_PREFIX}${estimateId}`);
      setLastExportInfo(raw ? JSON.parse(raw) : null);
    } catch { setLastExportInfo(null); }
  }, [estimates]);

  // Refresh last-export record whenever the popover opens OR the selected estimate changes
  useEffect(() => { readLastExport(selectedId); }, [selectedId, readLastExport]);
  useEffect(() => { if (exportOpen) readLastExport(selectedId); }, [exportOpen, selectedId, readLastExport]);

  const formatRelativeTime = (iso) => {
    if (!iso) return '';
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 0) return 'just now';
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.floor(hr / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  const exportPlanner = useCallback(async (format) => {
    if (!timeline) return;
    setExportOpen(false);
    try {
      const est = estimates.find(e => e.id === selectedId);
      const res = await axios.post(`${API_URL}/api/planner/export`, {
        currency_code: estimates.find(e => e.id === selectedId)?.currency_code || null,
        timeline_data: timeline,
        narrative: includeNarrative ? (narrative || null) : null,
        format,
        org_id: est?.org_id || null,
        estimate_name: est?.estimate_name || timeline.estimate_name || '',
        implementation_type: est?.implementation_type || '',
        complexity: est?.complexity || '',
      }, { withCredentials: true, responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'pptx' ? 'pptx' : format === 'docx' ? 'docx' : format === 'xlsx' ? 'xlsx' : 'pdf';
      const filename = `Resource_Plan.${ext}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // Persist last-export record (per estimate) for the popover audit footer
      if (selectedId) {
        const localRecord = { format, filename, timestamp: new Date().toISOString() };
        try { localStorage.setItem(`${LAST_EXPORT_PREFIX}${selectedId}`, JSON.stringify(localRecord)); } catch { /* quota */ }
        setLastExportInfo(localRecord);
        // Also persist server-side so other collaborators see who exported what & when
        try {
          const serverRes = await axios.post(
            `${API_URL}/api/estimates/${selectedId}/last-planner-export`,
            { format, filename },
            { withCredentials: true },
          );
          if (serverRes?.data) setLastExportInfo(serverRes.data);
        } catch { /* non-fatal — local record already surfaces the info */ }
      }
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  }, [timeline, narrative, selectedId, estimates, includeNarrative]);

  const fetchSavedPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/planner/saved`, { withCredentials: true });
      setSavedPlans(res.data.plans || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchSavedPlans(); }, [fetchSavedPlans]);

  // Goal Dates auto-linking: weeks between Goal Start and Go-Live (excludes Hypercare).
  // Uses the live timeline's Go-Live milestone week when available, else total duration.
  const goLiveWeekOffset = useMemo(() => {
    if (timeline?.milestones) {
      const go = timeline.milestones.find(m => m.name === 'Go Live');
      if (go?.week) return go.week;
    }
    if (timeline?.total_duration_weeks) return timeline.total_duration_weeks;
    return projectWeeks || null;
  }, [timeline, projectWeeks]);

  const shiftIsoDate = (dateStr, weeks) => {
    if (!dateStr) return '';
    const [y, m, dd] = dateStr.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1, dd));
    d.setUTCDate(d.getUTCDate() + weeks * 7);
    return d.toISOString().slice(0, 10);
  };

  const handleGoalStartChange = useCallback((value) => {
    setGoalStartDate(value);
    if (goalDatesLinked && value && goLiveWeekOffset && goLiveWeekOffset > 0) {
      setGoalGoLiveDate(shiftIsoDate(value, goLiveWeekOffset));
    }
  }, [goalDatesLinked, goLiveWeekOffset]);

  const handleGoalGoLiveChange = useCallback((value) => {
    setGoalGoLiveDate(value);
    if (goalDatesLinked && value && goLiveWeekOffset && goLiveWeekOffset > 0) {
      setGoalStartDate(shiftIsoDate(value, -goLiveWeekOffset));
    }
  }, [goalDatesLinked, goLiveWeekOffset]);

  // ─────────────────────────────────────────────────────────────────────
  // Smart Conflict Resolver — invoked from <ReleaseConflictCallout/> when
  // the user clicks "Shift Go-Live +N days". Preserves project duration
  // by bumping BOTH the goal start and goal Go-Live dates by `shiftDays`,
  // forces durationMode→'goals' (the only mode that respects both ends),
  // then regenerates the timeline. If we don't have a baseline start, we
  // bail out with a toast so the PM knows to set one first.
  // ─────────────────────────────────────────────────────────────────────
  const shiftIsoByDays = useCallback((iso, days) => {
    if (!iso) return null;
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d)) return null;
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  }, []);

  const handleShiftGoLive = useCallback((shiftDays, newGoLiveDate) => {
    if (!shiftDays || !selectedId) return;
    const baselineStart = goalStartDate || timeline?.goal_start_date || null;
    if (!baselineStart) {
      toast.error('Set a Goal Start Date first so we can shift the schedule.');
      return;
    }
    const newStart = shiftIsoByDays(baselineStart, shiftDays);
    const currentGoLive = goalGoLiveDate || (timeline?.milestones || []).find(m => (m?.name || '').toLowerCase().startsWith('go live'))?.date || null;
    const newGoLive = newGoLiveDate || (currentGoLive ? shiftIsoByDays(currentGoLive, shiftDays) : null);
    setDurationMode('goals');
    setGoalStartDate(newStart);
    if (newGoLive) setGoalGoLiveDate(newGoLive);
    // Defer regenerate until state is committed.
    setTimeout(() => {
      generateTimeline(selectedId);
      toast.success(
        `Go-Live shifted ${shiftDays > 0 ? '+' : ''}${shiftDays} day${Math.abs(shiftDays) === 1 ? '' : 's'} → ${newGoLive || newStart}. Conflicts cleared.`,
        { duration: 5000 }
      );
    }, 50);
  }, [selectedId, goalStartDate, goalGoLiveDate, timeline, shiftIsoByDays, generateTimeline]);

  const configSnapshot = useMemo(() => ({
    project_weeks: projectWeeks ?? null,
    resource_overrides: localFte || {},
    hours_per_day: localHpd,
    days_per_week: localDpw,
    utilization_pct: localUtil,
    complexity_week_presets: complexityPresets || {},
    duration_mode: durationMode,
    goal_start_date: goalStartDate || null,
    goal_golive_date: goalGoLiveDate || null,
    phase_dates: phaseDates || {},
  }), [projectWeeks, localFte, localHpd, localDpw, localUtil, complexityPresets, durationMode, goalStartDate, goalGoLiveDate, phaseDates]);

  const hasUnsavedChanges = useMemo(() => {
    if (!activePlanId || !savedBaseline) return false;
    return JSON.stringify(configSnapshot) !== JSON.stringify(savedBaseline);
  }, [activePlanId, savedBaseline, configSnapshot]);

  const hasResourceUnsaved = useMemo(() => {
    if (!activePlanId || !savedBaseline) return false;
    const keys = ['resource_overrides', 'hours_per_day', 'days_per_week', 'utilization_pct', 'complexity_week_presets'];
    return keys.some(k => JSON.stringify(configSnapshot[k]) !== JSON.stringify(savedBaseline[k]));
  }, [activePlanId, savedBaseline, configSnapshot]);

  // Warn before closing / refreshing when there are unsaved planner config tweaks
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // The save payload's `settings` block is derived from ~14 reactive
  // values — extracting it into a memo keeps `savePlan` from carrying
  // a 18-deep dependency array (and any future field is auto-picked
  // up by the memo without touching savePlan).
  const planSettingsPayload = useMemo(() => {
    const est = estimates.find(e => e.id === selectedId);
    return {
      project_weeks: projectWeeks,
      resource_overrides: localFte,
      hours_per_day: localHpd,
      days_per_week: localDpw,
      utilization_pct: localUtil,
      complexity_week_presets: complexityPresets,
      duration_mode: durationMode,
      goal_start_date: goalStartDate || null,
      goal_golive_date: goalGoLiveDate || null,
      phase_dates: phaseDates || {},
      estimate_name: est?.estimate_name || '',
      implementation_type: est?.implementation_type || '',
      complexity: est?.complexity || '',
    };
  }, [estimates, selectedId, projectWeeks, localFte, localHpd, localDpw, localUtil, complexityPresets, durationMode, goalStartDate, goalGoLiveDate, phaseDates]);

  const savePlan = useCallback(async () => {
    if (!timeline || !saveName.trim()) return;
    setSaving(true);
    try {
      const payload = {
        plan_name: saveName.trim(),
        estimate_id: selectedId,
        timeline_data: timeline,
        narrative: narrative || null,
        settings: planSettingsPayload,
        revision_note: revisionNote.trim() || null,
      };

      if (activePlanId) {
        const res = await axios.put(`${API_URL}/api/planner/saved/${activePlanId}`, payload, { withCredentials: true });
        toast.success(`Plan updated · v${res.data?.revision_number || '?'}`);
      } else {
        const res = await axios.post(`${API_URL}/api/planner/saved`, payload, { withCredentials: true });
        setActivePlanId(res.data.id);
        toast.success(`Plan saved · v${res.data?.revision_number || 1}`);
      }
      setSavedBaseline(JSON.parse(JSON.stringify(configSnapshot)));
      setSaveOpen(false);
      setRevisionNote('');
      fetchSavedPlans();
    } catch {
      toast.error('Failed to save plan');
    } finally {
      setSaving(false);
    }
  }, [timeline, narrative, saveName, revisionNote, selectedId, activePlanId, planSettingsPayload, configSnapshot, fetchSavedPlans]);

  // ── Revision History ────────────────────────────────────────────
  const openHistory = useCallback(async (planId, planName) => {
    setHistoryPlanId(planId);
    setHistoryPlanName(planName || 'Plan');
    setHistoryRevisions([]);
    setHistoryLoading(true);
    setCompareA(null);
    setCompareB(null);
    try {
      const res = await axios.get(`${API_URL}/api/planner/saved/${planId}/revisions`, { withCredentials: true });
      const revs = res.data?.revisions || [];
      setHistoryRevisions(revs);
      // Pre-seed compare slots: current vs immediately-prior so the diff
      // shows the latest change without the user having to click anything.
      if (revs.length >= 2) {
        setCompareA(revs[1].revision_number);  // prior
        setCompareB(revs[0].revision_number);  // current
      }
    } catch {
      toast.error('Failed to load revision history');
      setHistoryRevisions([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // ── Audit-trail CSV export ──────────────────────────────────────
  // Built client-side from the already-loaded revision list — no extra
  // backend round-trip. Fields chosen for PMO/compliance review.
  const exportRevisionsCsv = useCallback(() => {
    if (!historyRevisions.length) return;
    const escape = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const header = ['revision', 'saved_at_utc', 'saved_by_name', 'saved_by_email',
                    'note', 'restored_from_revision',
                    'total_effort_hours', 'total_cost', 'total_duration_weeks', 'peak_fte'];
    const rows = [...historyRevisions]
      .sort((a, b) => (a.revision_number || 0) - (b.revision_number || 0))
      .map(r => [
        r.revision_number,
        r.saved_at,
        r.saved_by_name || '',
        r.saved_by_email || '',
        r.note || '',
        r.restored_from_revision || '',
        r.summary?.total_effort_hours ?? '',
        r.summary?.total_cost ?? '',
        r.summary?.total_duration_weeks ?? '',
        r.summary?.peak_fte ?? '',
      ].map(escape).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const safeName = (historyPlanName || 'plan').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 60);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}_revisions_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${historyRevisions.length} revisions to CSV`);
  }, [historyRevisions, historyPlanName]);

  const restoreRevision = useCallback(async (planId, revNum) => {
    if (!planId || !revNum) return;
    setRestoringRev(revNum);
    try {
      const res = await axios.post(
        `${API_URL}/api/planner/saved/${planId}/revisions/${revNum}/restore`,
        {},
        { withCredentials: true },
      );
      toast.success(`Restored to v${revNum} · saved as v${res.data?.revision_number || '?'}`);
      // Refresh history list and active plan content if loaded
      const refreshed = await axios.get(`${API_URL}/api/planner/saved/${planId}/revisions`, { withCredentials: true });
      setHistoryRevisions(refreshed.data?.revisions || []);
      // If user has this plan loaded, reload it to reflect the restore
      if (activePlanId === planId) {
        const planRes = await axios.get(`${API_URL}/api/planner/saved/${planId}`, { withCredentials: true });
        if (planRes.data?.timeline_data) setTimeline(planRes.data.timeline_data);
        if (planRes.data?.narrative !== undefined) setNarrative(planRes.data.narrative);
      }
      fetchSavedPlans();
    } catch {
      toast.error(`Failed to restore v${revNum}`);
    } finally {
      setRestoringRev(null);
    }
  }, [activePlanId, fetchSavedPlans]);

  const loadPlan = useCallback(async (planId) => {
    try {
      const res = await axios.get(`${API_URL}/api/planner/saved/${planId}`, { withCredentials: true });
      const plan = res.data;
      setTimeline(plan.timeline_data);
      setNarrative(plan.narrative || null);
      setActivePlanId(planId);
      setSaveName(plan.plan_name || '');
      setSavedPlansOpen(false);
      const s = plan.settings || {};
      if (s.project_weeks) setProjectWeeks(s.project_weeks);
      if (s.estimate_id || plan.estimate_id) setSelectedId(s.estimate_id || plan.estimate_id);
      if (s.resource_overrides) setLocalFte(s.resource_overrides);
      if (s.hours_per_day) setLocalHpd(s.hours_per_day);
      if (s.days_per_week) setLocalDpw(s.days_per_week);
      if (s.utilization_pct) setLocalUtil(s.utilization_pct);
      if (s.complexity_week_presets) setComplexityPresets(s.complexity_week_presets);
      // Restore plan-mode + Goal Dates / Absolute Dates so they don't reset on reopen
      if (s.duration_mode) setDurationMode(s.duration_mode);
      else if (plan.timeline_data?.plan_mode === 'goals') setDurationMode('goals');
      else if (plan.timeline_data?.plan_mode === 'absolute') setDurationMode('absolute');
      setGoalStartDate(s.goal_start_date || plan.timeline_data?.goal_start_date || '');
      setGoalGoLiveDate(s.goal_golive_date || plan.timeline_data?.goal_golive_date || '');
      if (s.phase_dates) setPhaseDates(s.phase_dates);
      // Capture baseline exactly matching what was persisted so dirty-check starts clean
      setSavedBaseline({
        project_weeks: s.project_weeks ?? null,
        resource_overrides: s.resource_overrides || {},
        hours_per_day: s.hours_per_day || localHpd,
        days_per_week: s.days_per_week || localDpw,
        utilization_pct: s.utilization_pct || localUtil,
        complexity_week_presets: s.complexity_week_presets || complexityPresets || {},
        duration_mode: s.duration_mode || (plan.timeline_data?.plan_mode === 'goals' ? 'goals' : plan.timeline_data?.plan_mode === 'absolute' ? 'absolute' : 'preset'),
        goal_start_date: s.goal_start_date || plan.timeline_data?.goal_start_date || null,
        goal_golive_date: s.goal_golive_date || plan.timeline_data?.goal_golive_date || null,
        phase_dates: s.phase_dates || {},
      });
      toast.success(`Loaded: ${plan.plan_name}`);
    } catch {
      toast.error('Failed to load plan');
    }
  }, [localHpd, localDpw, localUtil, complexityPresets]);

  const discardChanges = useCallback(() => {
    if (!savedBaseline) return;
    const b = savedBaseline;
    if (b.project_weeks !== undefined) setProjectWeeks(b.project_weeks);
    if (b.resource_overrides) setLocalFte(b.resource_overrides);
    if (b.hours_per_day) setLocalHpd(b.hours_per_day);
    if (b.days_per_week) setLocalDpw(b.days_per_week);
    if (b.utilization_pct) setLocalUtil(b.utilization_pct);
    if (b.complexity_week_presets) setComplexityPresets(b.complexity_week_presets);
    if (b.duration_mode) setDurationMode(b.duration_mode);
    setGoalStartDate(b.goal_start_date || '');
    setGoalGoLiveDate(b.goal_golive_date || '');
    setPhaseDates(b.phase_dates || {});
    toast.success('Reverted to last saved plan');
  }, [savedBaseline]);

  const deletePlan = useCallback(async (planId) => {
    try {
      await axios.delete(`${API_URL}/api/planner/saved/${planId}`, { withCredentials: true });
      if (activePlanId === planId) { setActivePlanId(null); setSavedBaseline(null); }
      fetchSavedPlans();
      toast.success('Plan deleted');
    } catch {
      toast.error('Failed to delete plan');
    }
  }, [activePlanId, fetchSavedPlans]);

  const roleLabels = resourceDefaults?.role_labels || {};

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="planner-page">
      {/* ── Page Header ── */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold text-[#0F2040] flex items-center gap-2">Project Planner
            {!initialEstimateId && <PageHelpButton section="planner" label="Planner — open the user guide" />}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Timeline estimates & resource allocation</p>
        </div>
        <div className="flex items-center gap-2" data-testid="planner-actions-bar">
          <Button variant="outline" size="sm" onClick={captureSnapshot} disabled={snapshotting}
            className="text-[11px] text-[#0F2040] border-[#0F2040]/20 hover:bg-[#0F2040]/5 h-8 px-3" data-testid="snapshot-pdf-btn">
            {snapshotting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Camera className="w-3.5 h-3.5 mr-1.5" />}
            {snapshotting ? 'Capturing…' : 'Snapshot PDF'}
          </Button>
          {selectedId && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/estimator/quote/${selectedId}`)}
              className="text-[11px] text-[#4ABFCF] border-[#4ABFCF]/40 hover:bg-[#4ABFCF]/5 h-8 px-3 font-medium" data-testid="planner-quote-btn">
              <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Quote
            </Button>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm"
                className="text-slate-500 hover:text-[#0F2040] hover:bg-slate-100 h-8 w-8 p-0"
                title="More actions"
                data-testid="planner-more-actions-btn">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1" data-testid="planner-more-actions-menu">
              <EmailShareDialog
                onGeneratePdf={generateSnapshotBase64}
                defaultSubject={`Project Planner — ${TABS.find(t => t.id === activeTab)?.label || 'Overview'}`}
                defaultFilename={`Planner_${TABS.find(t => t.id === activeTab)?.label || 'Overview'}_${new Date().toISOString().slice(0, 10)}.pdf`}
                triggerLabel="Email snapshot…"
                triggerIcon={<Mail className="w-3.5 h-3.5 mr-2" />}
                triggerClassName="w-full justify-start text-xs text-slate-700 hover:bg-slate-50 h-9 px-2 font-normal"
                triggerVariant="ghost"
              />
              <button
                onClick={() => setTemplateBrowseOpen(true)}
                className="w-full flex items-center px-2 h-9 rounded-md text-xs text-slate-700 hover:bg-slate-50"
                data-testid="planner-templates-btn">
                <FolderOpen className="w-3.5 h-3.5 mr-2" /> Browse templates
              </button>
              {timeline && (
                <button
                  onClick={() => setTemplateSaveOpen(true)}
                  className="w-full flex items-center px-2 h-9 rounded-md text-xs text-slate-700 hover:bg-slate-50"
                  data-testid="planner-save-template-btn">
                  <Save className="w-3.5 h-3.5 mr-2" /> Save as template…
                </button>
              )}
              {timeline && (
                <button
                  onClick={() => navigate(`/math-reference?estimate_id=${selectedId}`)}
                  className="w-full flex items-center px-2 h-9 rounded-md text-xs text-slate-700 hover:bg-slate-50"
                  data-testid="planner-all-math-btn">
                  <BookOpen className="w-3.5 h-3.5 mr-2" /> All math reference
                </button>
              )}
              {selectedId && (
                <button
                  onClick={() => {
                    // Direct GET → triggers browser download. Cookie auth flows
                    // through the cross-origin request because we send same-site
                    // credentials on every prior /api/* fetch.
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/estimates/${selectedId}/gantt.png`;
                    window.open(url, '_blank');
                  }}
                  className="w-full flex items-center px-2 h-9 rounded-md text-xs text-slate-700 hover:bg-slate-50"
                  data-testid="planner-download-gantt-png-btn"
                  title="Download the implementation-phase Gantt as a PNG ready for PowerPoint."
                >
                  <Camera className="w-3.5 h-3.5 mr-2" /> Download Gantt PNG
                </button>
              )}
              <div className="my-1 border-t border-slate-100" />
              <button
                onClick={() => { resetGuides(); setGuideResetKey(k => k + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); toast.success('Guides restored'); }}
                className="w-full flex items-center px-2 h-9 rounded-md text-xs text-slate-500 hover:bg-slate-50"
                data-testid="reset-guides-btn">
                <HelpCircle className="w-3.5 h-3.5 mr-2" /> Show page guides
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* ── Sub-Navigation ── */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-5" data-testid="planner-subnav">
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          const disabled = tab.id !== 'overview' && tab.id !== 'whatif' && !timeline;
          return (
            <button
              key={tab.id}
              onClick={() => !disabled && switchTab(tab.id)}
              disabled={disabled}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                active
                  ? 'bg-white text-[#0F2040] shadow-sm'
                  : disabled
                    ? 'text-slate-300 cursor-not-allowed'
                    : 'text-slate-500 hover:text-[#0F2040] hover:bg-white/50'
              }`}
              data-testid={`planner-tab-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
              {disabled && !active && <span className="text-[9px] text-slate-300 ml-0.5">(generate first)</span>}
            </button>
          );
        })}
      </div>

      {/* ════════════════ TAB: OVERVIEW ════════════════ */}
      <div ref={tabContentRef}>
      {activeTab === 'overview' && (
        <div className="space-y-5">
          <SectionGuide id="planner-overview" title="How the Project Planner Works" accent="#0F2040" icon={BookOpen}>
            <p>
              The <strong>Project Planner</strong> transforms your LMS cost estimate into an actionable implementation timeline. It takes the components, effort hours, and complexity from your estimate and distributes them across project phases and weeks.
            </p>
            <p>
              <strong>How it works:</strong> Select an estimate, choose a duration (or Auto), and hit Generate. Use the <em>Timeline</em> and <em>Resources</em> tabs to explore the plan, and <em>What-If</em> to test alternatives.
            </p>
          </SectionGuide>

          {/* Estimate Selection & Duration */}
          <div className="flex items-center justify-between">
            <SectionGuide id="estimate-selection" title="Estimate Selection & Duration" accent="#0EA5E9" resetKey={guideResetKey}>
              <p>Select an estimate and project duration. Use <em>Auto</em> for dynamic duration or pick a preset.</p>
            </SectionGuide>
            {!hasConditions && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(!showSettings)}
                data-testid="toggle-settings-btn"
                className={hasResourceUnsaved ? 'relative border-amber-400 bg-amber-50 text-amber-800 shadow-[0_0_0_3px_rgba(251,191,36,0.18)] hover:bg-amber-100' : ''}
                title={hasResourceUnsaved ? 'Resource settings changed — unsaved' : undefined}
              >
                <Settings className="w-4 h-4 mr-1" /> {showSettings ? 'Hide' : 'Show'} Resource Settings
                {hasResourceUnsaved && (
                  <span
                    className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-white animate-pulse"
                    data-testid="resource-unsaved-dot"
                  />
                )}
              </Button>
            )}
          </div>

          {!hasConditions && showSettings && (
            <Card className="border-amber-200 bg-amber-50/30" data-testid="resource-settings">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-[#0F2040]">Resource & Working Assumptions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <SectionGuide id="resource-settings" title="Resource Configuration" accent="#F59E0B" resetKey={guideResetKey}>
                  <p>Adjust hours/day, days/week, and utilization to match real working patterns. FTE values set staff capacity per role.</p>
                </SectionGuide>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Hours / Day</Label>
                    <Input type="number" min={1} max={12} step={0.5} value={localHpd}
                      onChange={e => setLocalHpd(parseFloat(e.target.value) || 8)}
                      className="h-8 text-sm" data-testid="input-hpd" />
                  </div>
                  <div>
                    <Label className="text-xs">Days / Week</Label>
                    <Input type="number" min={1} max={7} step={0.5} value={localDpw}
                      onChange={e => setLocalDpw(parseFloat(e.target.value) || 5)}
                      className="h-8 text-sm" data-testid="input-dpw" />
                  </div>
                  <div>
                    <Label className="text-xs">Utilization %</Label>
                    <Input type="number" min={10} max={100} step={5} value={localUtil}
                      onChange={e => setLocalUtil(parseFloat(e.target.value) || 80)}
                      className="h-8 text-sm" data-testid="input-util" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-2 block">FTE Availability by Role</Label>
                  <div className="grid grid-cols-4 gap-3">
                    {Object.entries(localFte).map(([role, fte]) => (
                      <div key={role} className="flex items-center gap-2">
                        <span className="text-xs font-medium w-24 truncate" title={roleLabels[role] || role}>
                          <span className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ backgroundColor: ROLE_COLORS[role] || '#64748B' }} />
                          {roleLabels[role] || role}
                        </span>
                        <Input type="number" min={0.1} max={10} step={0.5} value={fte}
                          onChange={e => setLocalFte(prev => ({ ...prev, [role]: parseFloat(e.target.value) || 0.5 }))}
                          className="h-7 w-16 text-xs text-center" data-testid={`fte-${role}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card data-testid="estimate-selector">
            <CardContent className="pt-4 space-y-4">
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <Label className="text-xs font-semibold">Select Estimate</Label>
                  <Select value={selectedId} onValueChange={v => { setSelectedId(v); setCompareTimeline(null); setComparison(null); }}>
                    <SelectTrigger className="h-9 text-sm" data-testid="select-estimate">
                      <SelectValue placeholder="Choose an estimate..." />
                    </SelectTrigger>
                    <SelectContent>
                      {estimates.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.estimate_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-xs font-semibold">Compare With (optional)</Label>
                  <Select value={compareId} onValueChange={setCompareId}>
                    <SelectTrigger className="h-9 text-sm" data-testid="select-compare">
                      <SelectValue placeholder="Select to compare..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      {estimates.filter(e => e.id !== selectedId).map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.estimate_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Duration Selector — hidden when Project Conditions own the
                  schedule (the Estimator dictates duration via goal_duration_weeks
                  / breaks). A read-only summary chip replaces the controls so
                  users still understand WHY they can't tune it here. */}
              {hasConditions ? (
                <div
                  className="border-t pt-3 flex items-center justify-between gap-3 text-xs text-slate-600"
                  data-testid="duration-locked-notice"
                >
                  <div className="flex items-start gap-2 min-w-0">
                    <Lock className="w-4 h-4 text-[#4ABFCF] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0F2040]">Duration locked by Project Conditions</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Auto-derived from goal duration{projectConditions?.goal_duration_weeks ? ` (${projectConditions.goal_duration_weeks}w)` : ''}, breaks &amp; blackouts. Edit via <span className="font-semibold text-[#0F2040]">Estimate</span> to change.
                      </div>
                      {(conditionsImpact.extraWeeks > 0 || conditionsImpact.overlappingCount > 0) && (
                        <div
                          className="flex flex-wrap items-center gap-1.5 mt-1.5"
                          data-testid="conditions-impact-chips"
                        >
                          {conditionsImpact.extraWeeks > 0 && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#4ABFCF]/10 ring-1 ring-[#4ABFCF]/30 text-[10px] font-medium text-[#0F2040]"
                              title={`Honored breaks add ${conditionsImpact.extraWeeks} week${conditionsImpact.extraWeeks === 1 ? '' : 's'} to the project span.`}
                              data-testid="conditions-impact-extra-weeks"
                            >
                              +{conditionsImpact.extraWeeks}w from {conditionsImpact.breakLabels.length === 1
                                ? conditionsImpact.breakLabels[0]
                                : `${conditionsImpact.breakLabels.length} break${conditionsImpact.breakLabels.length === 1 ? '' : 's'}`}
                            </span>
                          )}
                          {conditionsImpact.overlappingCount > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                window.dispatchEvent(new CustomEvent('open-project-conditions', {
                                  detail: { breakId: conditionsImpact.firstOverlapBreakId || null },
                                }));
                              }}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 ring-1 ring-amber-300/80 text-[10px] font-medium text-amber-800 hover:bg-amber-200 hover:ring-amber-400 transition-colors cursor-pointer"
                              title="Click to open Project Conditions and jump to the colliding break."
                              data-testid="conditions-impact-overlap"
                            >
                              <AlertTriangle className="w-2.5 h-2.5" />
                              {conditionsImpact.overlappingCount} component{conditionsImpact.overlappingCount === 1 ? '' : 's'} overlap{conditionsImpact.overlappingCount === 1 ? 's' : ''}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs shrink-0"
                    onClick={() => navigate(`/estimator?load=${selectedId}`)}
                    data-testid="locked-edit-in-estimate-btn"
                  >
                    <FileEdit className="w-3.5 h-3.5 mr-1" /> Edit in Estimate
                  </Button>
                </div>
              ) : (
                <div className="border-t pt-3" data-testid="duration-selector">
                <div className="flex items-center justify-between mb-2 gap-3">
                  <Label className="text-xs font-semibold text-[#0F2040]">Project Duration</Label>
                  {/* Mode picker compacted from 4-button toggle to a Select
                      so users see one decision instead of four. The
                      revealed sub-controls still adapt by mode. */}
                  <Select value={durationMode} onValueChange={setDurationMode}>
                    <SelectTrigger className="h-7 w-44 text-[11px]" data-testid="duration-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="preset" data-testid="duration-mode-preset">Preset · pick a complexity</SelectItem>
                      <SelectItem value="manual" data-testid="duration-mode-manual">Manual · drag a slider</SelectItem>
                      <SelectItem value="goals" data-testid="duration-mode-goals">Goal Dates · start + go-live</SelectItem>
                      <SelectItem value="absolute" data-testid="duration-mode-absolute">Absolute · per-phase dates</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Preset mode */}
                {durationMode === 'preset' && (
                  <div className="flex items-center gap-2">
                    <button className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${projectWeeks === null ? 'bg-[#0F2040] text-white border-[#0F2040] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#0F2040] hover:text-[#0F2040]'}`}
                      onClick={() => setProjectWeeks(null)} data-testid="preset-auto">
                      <div>Auto</div>
                      <div className={`text-[10px] mt-0.5 ${projectWeeks === null ? 'text-slate-300' : 'text-slate-400'}`}>Dynamic</div>
                    </button>
                    {Object.entries(complexityPresets).map(([key, weeks]) => {
                      const isActive = projectWeeks === weeks;
                      return (
                        <button key={key} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${isActive ? 'bg-[#0F2040] text-white border-[#0F2040] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#0F2040] hover:text-[#0F2040]'}`}
                          onClick={() => setProjectWeeks(isActive ? null : weeks)} data-testid={`preset-${key}`}>
                          <div className="capitalize">{key}</div>
                          <div className={`text-[10px] mt-0.5 ${isActive ? 'text-slate-300' : 'text-slate-400'}`}>{weeks} weeks</div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Manual mode */}
                {durationMode === 'manual' && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <Slider value={[projectWeeks || 10]} onValueChange={([v]) => setProjectWeeks(v)} min={4} max={52} step={1} className="w-full" data-testid="duration-slider" />
                      <div className="flex justify-between text-[9px] text-slate-400 mt-0.5 px-0.5"><span>4w</span><span>52w</span></div>
                    </div>
                    <Input type="number" min={4} max={52} value={projectWeeks || 10}
                      onChange={e => { const v = parseInt(e.target.value) || 10; setProjectWeeks(Math.max(4, Math.min(52, v))); }}
                      className="w-20 h-8 text-sm text-center" data-testid="duration-input" />
                    <span className="text-xs text-slate-500 shrink-0">weeks</span>
                  </div>
                )}

                {/* Goal Dates mode */}
                {durationMode === 'goals' && (
                  <div className="space-y-2" data-testid="goal-dates-panel">
                    <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
                      <div>
                        <label className="text-[10px] font-medium text-slate-500 mb-1 block">Goal Start Date</label>
                        <DatePickerField value={goalStartDate} onChange={(iso) => handleGoalStartChange(iso)}
                          testId="goal-start-date" placeholder="Pick start" widthClass="w-full" />
                      </div>
                      <button
                        type="button"
                        onClick={() => setGoalDatesLinked(v => !v)}
                        className={`h-8 w-8 flex items-center justify-center rounded-md border transition-colors ${goalDatesLinked ? 'border-[#0F2040]/30 text-[#0F2040] bg-[#0F2040]/5 hover:bg-[#0F2040]/10' : 'border-slate-300 text-slate-400 bg-white hover:bg-slate-50'}`}
                        title={goalDatesLinked ? 'Linked — editing one date auto-fills the other. Click to unlink.' : 'Unlinked — dates are independent. Click to re-link.'}
                        data-testid="goal-dates-link-toggle"
                        aria-pressed={goalDatesLinked}
                      >
                        {goalDatesLinked ? <Link2 className="w-3.5 h-3.5" /> : <Link2Off className="w-3.5 h-3.5" />}
                      </button>
                      <div>
                        <label className="text-[10px] font-medium text-slate-500 mb-1 block">Goal Go-Live Date</label>
                        <DatePickerField value={goalGoLiveDate} onChange={(iso) => handleGoalGoLiveChange(iso)}
                          testId="goal-golive-date" placeholder="Pick go-live" widthClass="w-full"
                          minDate={goalStartDate || undefined} anchorDate={goalStartDate || undefined} quickRanges />
                      </div>
                    </div>
                    {goLiveWeekOffset > 0 && (
                      <p className="text-[10px] text-slate-400" data-testid="goal-dates-link-hint">
                        {goalDatesLinked ? (
                          <>Start ↔ Go-Live are linked at <span className="font-semibold text-[#0F2040]">{goLiveWeekOffset} weeks</span> (project timeline to Go-Live, excluding Hypercare). Edit either field to auto-calculate the other.</>
                        ) : (
                          <>Link broken — Start and Go-Live can be set independently (tasks will distribute proportionally within the custom window). Click the <Link2Off className="inline w-2.5 h-2.5 align-middle" /> icon to re-link at <span className="font-semibold text-[#0F2040]">{goLiveWeekOffset} weeks</span>.</>
                        )}
                      </p>
                    )}
                    {goalStartDate && goalGoLiveDate && (
                      <div className="flex items-center gap-2 p-2 rounded-md bg-[#0F2040]/5 border border-[#0F2040]/10">
                        <Target className="w-3.5 h-3.5 text-[#0F2040]" />
                        <span className="text-xs text-[#0F2040]">
                          <span className="font-semibold">{Math.max(1, Math.round((new Date(goalGoLiveDate) - new Date(goalStartDate)) / (7 * 86400000)))} weeks</span>
                          &nbsp;&mdash; Tasks will be proportionally distributed within this window.
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Absolute Dates mode */}
                {durationMode === 'absolute' && (
                  <div className="space-y-2" data-testid="absolute-dates-panel">
                    <p className="text-[10px] text-slate-400">Set start/end dates per phase. Expand to override individual components.</p>
                    {timeline ? (
                      <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
                        {timeline.group_schedule.map(grp => {
                          const groupName = grp.group;
                          const pd = phaseDates[groupName] || {};
                          const isExpanded = expandedPhase === groupName;
                          return (
                            <div key={groupName} className="border rounded-lg overflow-hidden" data-testid={`phase-date-${groupName.replace(/\s/g, '-').toLowerCase()}`}>
                              <div className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 hover:bg-slate-100 transition-colors">
                                <button onClick={() => setExpandedPhase(isExpanded ? null : groupName)} className="text-slate-400 hover:text-slate-600">
                                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                </button>
                                <span className="text-xs font-medium text-[#0F2040] flex-1 truncate">{groupName}</span>
                                <Input type="date" value={pd.start || ''} onChange={e => {
                                  setPhaseDates(prev => ({...prev, [groupName]: {...(prev[groupName] || {}), start: e.target.value}}));
                                }} className="h-6 text-[10px] w-[115px] px-1" data-testid={`phase-start-${groupName.replace(/\s/g, '-').toLowerCase()}`} />
                                <span className="text-[10px] text-slate-300">&rarr;</span>
                                <Input type="date" value={pd.end || ''} onChange={e => {
                                  setPhaseDates(prev => ({...prev, [groupName]: {...(prev[groupName] || {}), end: e.target.value}}));
                                }} className="h-6 text-[10px] w-[115px] px-1" data-testid={`phase-end-${groupName.replace(/\s/g, '-').toLowerCase()}`} />
                              </div>
                              {isExpanded && (
                                <div className="px-3 py-1.5 bg-white border-t space-y-1">
                                  {(grp.components || []).map(comp => {
                                    const cd = (pd.components || {})[comp.name] || {};
                                    return (
                                      <div key={comp.name} className="flex items-center gap-2">
                                        <span className="text-[10px] text-slate-500 flex-1 truncate pl-3">{comp.name}</span>
                                        <Input type="date" value={cd.start || pd.start || ''} onChange={e => {
                                          setPhaseDates(prev => {
                                            const group = prev[groupName] || {};
                                            const comps = group.components || {};
                                            return {...prev, [groupName]: {...group, components: {...comps, [comp.name]: {...(comps[comp.name] || {}), start: e.target.value}}}};
                                          });
                                        }} className="h-5 text-[10px] w-[110px] px-1" />
                                        <span className="text-[10px] text-slate-300">&rarr;</span>
                                        <Input type="date" value={cd.end || pd.end || ''} onChange={e => {
                                          setPhaseDates(prev => {
                                            const group = prev[groupName] || {};
                                            const comps = group.components || {};
                                            return {...prev, [groupName]: {...group, components: {...comps, [comp.name]: {...(comps[comp.name] || {}), end: e.target.value}}}};
                                          });
                                        }} className="h-5 text-[10px] w-[110px] px-1" />
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        <span className="text-[10px] text-amber-700">Generate a plan first (using Preset or Manual) to see phases, then switch to Absolute to assign dates.</span>
                      </div>
                    )}
                  </div>
                )}

                <p className="text-[10px] text-slate-400 mt-1.5">
                  {durationMode === 'preset' && (projectWeeks
                    ? <>Resource allocation will be distributed across <span className="font-semibold text-[#0F2040]">{projectWeeks} weeks</span>.</>
                    : <><span className="font-semibold text-[#0F2040]">Auto mode:</span> Duration calculated dynamically based on effort delta and project factor adjustments.</>
                  )}
                  {durationMode === 'manual' && <>Resource allocation will be distributed across <span className="font-semibold text-[#0F2040]">{projectWeeks || 10} weeks</span>.</>}
                  {durationMode === 'goals' && <><span className="font-semibold text-[#0F2040]">Goal Dates:</span> Set start and go-live dates. Tasks auto-distribute to fit within the window.</>}
                  {durationMode === 'absolute' && <><span className="font-semibold text-[#0F2040]">Absolute Dates:</span> Set specific dates per phase with optional component-level overrides.</>}
                </p>
              </div>
              )}

              {!hasConditions && (
                <Button onClick={() => compareId && compareId !== 'none' ? generateComparison() : generateTimeline(selectedId)}
                  disabled={!selectedId || loading} className="w-full" data-testid="generate-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Calendar className="w-4 h-4 mr-1" />}
                  Generate {projectWeeks ? `(${projectWeeks}w)` : ''}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* ── Timeline Generated Results ── */}
          {timeline && (
            <div className="space-y-5" data-testid="timeline-results">
              {/* Summary Cards + Actions */}
              <div className="flex items-start justify-between gap-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 flex-1">
                  {(() => {
                    const startISO = goalStartDate || timeline.goal_start_date;
                    const progress = buildProjectProgress(startISO, timeline.total_duration_weeks);
                    const dateRange = (timeline.project_start_date && timeline.project_end_date)
                      ? `${new Date(timeline.project_start_date + 'T00:00:00').toLocaleDateString('en-US', {month:'short', day:'numeric', year:'2-digit'})} – ${new Date(timeline.project_end_date + 'T00:00:00').toLocaleDateString('en-US', {month:'short', day:'numeric', year:'2-digit'})}`
                      : (timeline.project_weeks && timeline.project_weeks !== timeline.total_duration_weeks ? `(${timeline.project_weeks}w target)` : null);
                    let progressLabel = null;
                    if (progress?.state === 'during') {
                      progressLabel = `T${progress.currentWeek}/${progress.totalWeeks}w · ${progress.pctComplete}% complete · ${progress.weeksRemaining}w left`;
                    } else if (progress?.state === 'before') {
                      progressLabel = `Starts in ${progress.weeksUntilStart}w`;
                    } else if (progress?.state === 'after') {
                      progressLabel = 'Project span complete';
                    }
                    const subtext = [dateRange, progressLabel].filter(Boolean).join(' · ');
                    return (
                      <SummaryCard
                        icon={Calendar}
                        label="Duration"
                        value={`${timeline.total_duration_weeks} weeks`}
                        delta={comparison?.duration_delta}
                        suffix=" wks"
                        subtext={subtext || null}
                      />
                    );
                  })()}
                  <SummaryCard icon={Clock} label="Total Effort" value={`${timeline.total_effort_hours.toLocaleString()} hrs`}
                    delta={comparison?.effort_delta} suffix=" hrs" />
                  <SummaryCard icon={Users} label="Peak FTE" value={timeline.peak_fte}
                    delta={comparison?.peak_fte_delta} suffix=" FTE" />
                  <SummaryCard icon={BarChart3} label="Avg FTE" value={timeline.avg_fte} />
                  <SummaryCard icon={TrendingUp} label="Peak Hrs/Week" value={`${timeline.peak_hours_per_week || 0} hrs`} />
                </div>
                <div className="flex flex-col gap-2 shrink-0 mt-1">
                  <Popover open={exportOpen} onOpenChange={setExportOpen}>
                    <PopoverTrigger asChild>
                      <Button className="bg-[#0F2040] hover:bg-[#1A3055] text-white w-full" size="sm" data-testid="export-btn">
                        <Download className="w-4 h-4 mr-1.5" /> Export
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-56 p-0" data-testid="export-menu">
                      <div className="px-3 py-2.5 border-b"><p className="text-sm font-semibold text-[#0F2040]">Export Format</p></div>
                      <div className="py-1">
                        <button onClick={() => exportPlanner('pdf')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="export-pdf-btn">
                          <FileText className="w-4.5 h-4.5 text-red-500" /> PDF Document
                        </button>
                        <button onClick={() => exportPlanner('pptx')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="export-pptx-btn">
                          <Presentation className="w-4.5 h-4.5 text-orange-500" /> PowerPoint
                        </button>
                        <button onClick={() => exportPlanner('docx')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="export-docx-btn">
                          <FileType className="w-4.5 h-4.5 text-blue-500" /> Word Document
                        </button>
                        <button onClick={() => exportPlanner('xlsx')} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="export-xlsx-btn">
                          <FileSpreadsheet className="w-4.5 h-4.5 text-emerald-600" /> Excel Workbook
                        </button>
                      </div>
                      <div className="px-3 py-2.5 border-t flex items-center justify-between">
                        <label htmlFor="include-narrative" className="text-xs font-medium text-slate-600 cursor-pointer">Include AI Narrative</label>
                        <Switch id="include-narrative" checked={includeNarrative} onCheckedChange={setIncludeNarrative}
                          className="data-[state=checked]:bg-[#0F2040]" data-testid="include-narrative-toggle" />
                      </div>
                      {lastExportInfo && (
                        <div className="px-3 py-2 border-t bg-slate-50/70 flex items-center gap-2 text-[10.5px] text-slate-500" data-testid="last-export-footer">
                          <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                          <span className="truncate">
                            Last exported <span className="font-semibold text-slate-700">{formatRelativeTime(lastExportInfo.timestamp)}</span>
                            {lastExportInfo.exported_by_name && (
                              <> by <span className="font-semibold text-slate-700">{lastExportInfo.exported_by_name}</span></>
                            )}
                            &nbsp;·&nbsp;
                            <span className="font-mono text-slate-600">{lastExportInfo.filename}</span>
                          </span>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>

                  <Popover open={saveOpen} onOpenChange={(open) => {
                    if (hasConditions) return; // Read-only when Project Conditions own the plan.
                    setSaveOpen(open);
                    if (open && !saveName) {
                      const est = estimates.find(e => e.id === selectedId);
                      setSaveName(est?.estimate_name ? `${est.estimate_name} — Plan` : 'Resource Plan');
                    }
                  }}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs relative"
                        disabled={hasConditions}
                        title={hasConditions ? 'Plan locked — derived from Project Conditions on the Estimate' : undefined}
                        data-testid="save-plan-btn"
                      >
                        <Save className="w-3.5 h-3.5 mr-1.5" /> {activePlanId ? 'Update Plan' : 'Save Plan'}
                        {hasUnsavedChanges && !hasConditions && (
                          <span
                            className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-white animate-pulse"
                            title="Unsaved changes — click Update Plan to persist"
                            data-testid="unsaved-indicator"
                          />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-72 p-0" data-testid="save-menu">
                      <div className="px-3 py-2.5 border-b">
                        <p className="text-sm font-semibold text-[#0F2040]">{activePlanId ? 'Update Plan' : 'Save Plan'}</p>
                        {activePlanId && (
                          <p className="text-[10px] text-slate-400 mt-0.5">A new revision will be added to this plan's history.</p>
                        )}
                      </div>
                      <div className="p-3 space-y-2.5">
                        <div>
                          <Label className="text-xs text-slate-500">Plan Name</Label>
                          <Input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="Enter plan name..."
                            className="h-8 text-sm mt-1" data-testid="save-plan-name" />
                        </div>
                        {activePlanId && (
                          <div>
                            <Label className="text-xs text-slate-500">What changed? <span className="text-slate-300 font-normal">(optional)</span></Label>
                            <Input
                              value={revisionNote}
                              onChange={e => setRevisionNote(e.target.value)}
                              placeholder="e.g. lifted PM weighting +10%"
                              maxLength={240}
                              className="h-8 text-sm mt-1"
                              data-testid="save-plan-revision-note"
                            />
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button size="sm" className="flex-1 bg-[#0F2040] hover:bg-[#1A3055] text-white text-xs"
                            onClick={savePlan} disabled={!saveName.trim() || saving} data-testid="save-plan-confirm">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                            {activePlanId ? 'Update' : 'Save'}
                          </Button>
                          {activePlanId && (
                            <Button size="sm" variant="outline" className="text-xs"
                              onClick={() => { setActivePlanId(null); setSavedBaseline(null); setSaveName(''); setRevisionNote(''); }} data-testid="save-as-new-btn">
                              Save as New
                            </Button>
                          )}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Saved Plans */}
              {savedPlans.length > 0 && (() => {
                const stalePlans = savedPlans.filter(p => {
                  const est = estimates.find(e => e.id === p.estimate_id);
                  const estTs = est?.updated_at || est?.created_at;
                  return !!(estTs && p.updated_at && new Date(estTs) > new Date(p.updated_at));
                });
                const staleCount = stalePlans.length;
                return (
                <div className="flex items-center gap-2" data-testid="saved-plans-strip">
                  <Popover open={savedPlansOpen} onOpenChange={setSavedPlansOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-xs text-slate-500 hover:text-[#0F2040] relative" data-testid="load-plan-btn">
                        <FolderOpen className="w-3.5 h-3.5 mr-1" /> Saved Plans ({savedPlans.length})
                        {staleCount > 0 && (
                          <span
                            className="ml-1.5 text-[9px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 rounded-full"
                            title={`${staleCount} plan${staleCount === 1 ? '' : 's'} out of sync with source estimate`}
                            data-testid="stale-plans-count"
                          >
                            {staleCount} stale
                          </span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-96 p-0" data-testid="saved-plans-menu">
                      <div className="px-3 py-2.5 border-b"><p className="text-sm font-semibold text-[#0F2040]">Saved Plans</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Each plan keeps its last {20} revisions — open History to inspect or restore.</p>
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        {savedPlans.map(p => {
                          const est = estimates.find(e => e.id === p.estimate_id);
                          const estTs = est?.updated_at || est?.created_at;
                          const isStale = !!(estTs && p.updated_at && new Date(estTs) > new Date(p.updated_at));
                          const revCount = p.revisions_count || 0;
                          const revLabel = revCount > 1 ? `v${p.revision_number || revCount}` : 'v1';
                          const updatedBy = p.updated_by_name || p.created_by_name || p.created_by || '—';
                          const updatedAtRel = p.updated_at ? new Date(p.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                          return (
                          <div key={p.id} className={`flex items-start justify-between px-3 py-2 hover:bg-slate-50 ${activePlanId === p.id ? 'bg-blue-50' : ''}`} data-testid={`saved-plan-${p.id}`}>
                            <button className="flex-1 text-left min-w-0" onClick={() => loadPlan(p.id)}>
                              <p className="text-sm font-medium text-[#0F2040] truncate flex items-center gap-1.5">
                                {isStale && (
                                  <span
                                    className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0"
                                    title="Source estimate was modified after this plan was saved — reload to refresh"
                                    data-testid={`plan-stale-${p.id}`}
                                  />
                                )}
                                <span className="truncate">{p.plan_name}</span>
                                <span
                                  className="text-[9px] font-bold text-blue-700 bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded shrink-0"
                                  title={`${revCount} revision${revCount === 1 ? '' : 's'} on file`}
                                  data-testid={`plan-rev-${p.id}`}
                                >
                                  {revLabel}
                                </span>
                              </p>
                              <p className="text-[10px] text-slate-400 truncate">
                                Updated {updatedAtRel} · by {updatedBy}
                                {isStale && <span className="text-amber-600 font-medium"> · estimate updated</span>}
                              </p>
                            </button>
                            <div className="flex items-center gap-0.5 shrink-0 ml-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); openHistory(p.id, p.plan_name); }}
                                className="p-1 text-slate-400 hover:text-[#0F2040]"
                                title="View revision history"
                                data-testid={`history-plan-${p.id}`}
                              >
                                <History className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => deletePlan(p.id)} className="p-1 text-slate-300 hover:text-red-500" data-testid={`delete-plan-${p.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {activePlanId && (
                    <div className="flex items-center gap-1.5" data-testid="active-plan-badge-wrap">
                      <Badge variant="outline" className={`text-[10px] ${hasUnsavedChanges ? 'text-amber-700 border-amber-300 bg-amber-50' : 'text-[#0F2040] border-[#0F2040]/20 bg-[#0F2040]/5'}`} data-testid="active-plan-badge">
                        {hasUnsavedChanges && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5 inline-block animate-pulse" />}
                        {saveName || 'Saved'}{hasUnsavedChanges ? ' • Unsaved' : ''}
                      </Badge>
                      {hasUnsavedChanges && (
                        <button
                          onClick={discardChanges}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-100 transition-colors"
                          title="Revert all planning-config fields back to the last saved plan"
                          data-testid="discard-changes-btn"
                        >
                          Discard
                        </button>
                      )}
                    </div>
                  )}
                </div>
                );
              })()}

              {/* Source of Truth (Planner-as-Renderer) */}
              {timeline.source_of_truth && (
                <Card data-testid="source-of-truth-panel" className="border-emerald-200/70 bg-emerald-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
                      <Shield className="w-4 h-4 text-emerald-600" /> Source of Truth
                      <Badge variant="outline" className="text-[10px] ml-1 bg-white/70 border-emerald-300 text-emerald-700">Planner-as-Renderer</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
                      The Planner does not invent numbers. Effort &amp; cost come straight from the saved Estimate; duration is derived from Project Conditions. Adjusting conditions changes <em>when</em> hours fall on the calendar, never <em>how many</em>.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="bg-white rounded-lg p-3 border border-emerald-100" data-testid="sot-effort">
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="w-3.5 h-3.5 text-[#0F2040]" />
                          <p className="text-[10px] text-slate-500 uppercase tracking-wide">Effort</p>
                          {timeline.source_of_truth.effort.matches_estimate ? (
                            <Badge className="ml-auto bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0 text-[9px] py-0 px-1.5">matches Estimate</Badge>
                          ) : (
                            <Badge className="ml-auto bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 text-[9px] py-0 px-1.5">distributed differs</Badge>
                          )}
                        </div>
                        <p className="text-base font-bold text-[#0F2040]">{timeline.source_of_truth.effort.value.toLocaleString(undefined, {maximumFractionDigits: 1})} hrs</p>
                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">estimate.results.total_effort</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-emerald-100" data-testid="sot-cost">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-3.5 h-3.5 text-[#0F2040]" />
                          <p className="text-[10px] text-slate-500 uppercase tracking-wide">Cost</p>
                        </div>
                        <p className="text-base font-bold text-[#0F2040]">{getCurrencySymbol(estimates.find(e => e.id === selectedId)?.currency_code)}{Math.round(timeline.source_of_truth.cost.value).toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">estimate.results.total_cost</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-emerald-100" data-testid="sot-duration">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-3.5 h-3.5 text-[#0F2040]" />
                          <p className="text-[10px] text-slate-500 uppercase tracking-wide">Duration</p>
                          <Badge className="ml-auto bg-slate-100 text-slate-600 hover:bg-slate-100 border-0 text-[9px] py-0 px-1.5">
                            {timeline.source_of_truth.duration.source === 'project_conditions' ? 'project conditions' : 'auto-projected'}
                          </Badge>
                        </div>
                        <p className="text-base font-bold text-[#0F2040]">{timeline.source_of_truth.duration.weeks} weeks</p>
                        <div className="text-[10px] text-slate-400 mt-0.5 space-y-0.5">
                          {timeline.source_of_truth.duration.components.goal_duration_weeks ? (
                            <div>Goal: {timeline.source_of_truth.duration.components.goal_duration_weeks}w</div>
                          ) : null}
                          {timeline.source_of_truth.duration.components.break_extension_weeks ? (
                            <div>+{timeline.source_of_truth.duration.components.break_extension_weeks}w breaks/freeze</div>
                          ) : null}
                          {timeline.source_of_truth.duration.components.user_override ? (
                            <div className="text-blue-600">Manual override</div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Timing Analysis */}
              {timeline.timing_analysis && (
                <Card data-testid="timing-analysis">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-sm font-semibold text-[#0F2040]">Timing Analysis</CardTitle>
                      <MathExplainer title="Duration Calculation" iconSize={3.5} testId="math-timing-info">
                        <p>Project duration is dynamically calculated from the complexity baseline, effort delta, and project factor adjustments.</p>
                        <MathSection title="Formula">
                          <FormulaBlock lines={[
                            'Effort Multiplier = (1 + timing_increase_pct) ^ increments',
                            '  where increments = floor(effort_delta / increment_hours)',
                            '', 'Factor Product = product of all project factor values',
                            '', 'Timing Multiplier = Effort Multiplier × Factor Product',
                            'Adjusted Weeks = round(Base Weeks × Timing Multiplier)',
                          ]} />
                        </MathSection>
                        <MathSection title="Your Calculation">
                          <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                            <CalcStep label="Complexity" value={timeline.complexity || '—'} />
                            <CalcStep label="Base Weeks" value={`${timeline.timing_analysis.base_weeks}w`} />
                            <CalcStep label="Effort Delta" value={`+${timeline.timing_analysis.effort_delta} hrs`} />
                            <CalcStep label="Timing Multiplier" value={`×${timeline.timing_analysis.timing_multiplier}`} />
                            <CalcStep label="= Final Duration" value={`${timeline.timing_analysis.adjusted_weeks} weeks`} highlight bold />
                          </div>
                        </MathSection>
                      </MathExplainer>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">Complexity</p>
                        <p className="text-sm font-semibold text-[#0F2040] capitalize mt-0.5">{timeline.complexity || '—'}</p>
                        <p className="text-[10px] text-slate-400">Base: {timeline.timing_analysis.base_weeks}w</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3" data-testid="duration-driver-effort">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">Effort vs Baseline</p>
                        <p className="text-sm font-semibold text-[#0F2040] mt-0.5">+{timeline.timing_analysis.effort_delta} hrs</p>
                        <p className="text-[10px] text-slate-400">{timeline.timing_analysis.effort_increments} increment{timeline.timing_analysis.effort_increments !== 1 ? 's' : ''} → duration only</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">Factor Adjustment</p>
                        <p className="text-sm font-semibold text-[#0F2040] mt-0.5">{`\u00D7${timeline.timing_analysis.factor_product}`}</p>
                        <p className="text-[10px] text-slate-400">Project modifiers</p>
                      </div>
                      <div className={`rounded-lg p-3 ${timeline.timing_analysis.user_override ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">Final Duration</p>
                        <p className="text-sm font-semibold text-[#0F2040] mt-0.5">{timeline.timing_analysis.adjusted_weeks}w</p>
                        <p className="text-[10px] text-slate-400">{timeline.timing_analysis.user_override ? 'Manual override' : `${`\u00D7${timeline.timing_analysis.timing_multiplier}`} from base`}</p>
                      </div>
                    </div>
                    {timeline.hosting_model === 'private_cloud' && (
                      <div className="mt-2 flex items-center gap-2 text-[10px] text-amber-700 bg-amber-50 rounded-md px-3 py-1.5">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        Private Cloud: 32 hrs Infrastructure Configuration forced into Week 1
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Integration Scope */}
              {(() => {
                const selEst = estimates.find(e => e.id === selectedId);
                const enabledIntg = (selEst?.integrations || []).filter(i => i.enabled);
                if (enabledIntg.length === 0) return null;
                return (
                  <Card data-testid="planner-integration-scope">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
                        <Shield className="w-4 h-4 text-indigo-500" /> Integration Scope
                        <Badge variant="outline" className="text-[10px] ml-1">{enabledIntg.length}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-[10px] text-slate-400 mb-2">Integrations captured during estimate creation. These impact timeline, testing, and resource needs.</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {enabledIntg.map((intg) => (
                          <div key={intg.label} className="flex items-center gap-2 p-2 bg-indigo-50/50 rounded-md border border-indigo-100">
                            <Check className="w-3 h-3 text-indigo-500 shrink-0" />
                            <span className="text-xs font-medium text-slate-700 truncate">{intg.label}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* AI Narrative */}
              <PlannerNarrative narrative={narrative} loading={narrativeLoading} expanded={narrativeExpanded}
                onToggle={() => setNarrativeExpanded(!narrativeExpanded)} onGenerate={generateNarrative} />

              {/* Comparison Panel */}
              {comparison && compareTimeline && (
                <Card className="border-teal-200" data-testid="comparison-panel">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#0F2040]">Timeline Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4">
                      {[
                        { label: 'Duration', base: `${timeline.total_duration_weeks} wks`, comp: `${compareTimeline.total_duration_weeks} wks`, delta: comparison.duration_delta, suffix: ' wks' },
                        { label: 'Total Effort', base: `${timeline.total_effort_hours} hrs`, comp: `${compareTimeline.total_effort_hours} hrs`, delta: comparison.effort_delta, suffix: ' hrs' },
                        { label: 'Peak FTE', base: timeline.peak_fte, comp: compareTimeline.peak_fte, delta: comparison.peak_fte_delta, suffix: ' FTE' },
                        { label: 'Total Cost', base: `${getCurrencySymbol(estimates.find(e => e.id === selectedId)?.currency_code)}${timeline.total_cost.toLocaleString()}`, comp: `${getCurrencySymbol(estimates.find(e => e.id === selectedId)?.currency_code)}${compareTimeline.total_cost.toLocaleString()}`, delta: comparison.cost_delta, suffix: '', prefix: getCurrencySymbol(estimates.find(e => e.id === selectedId)?.currency_code) },
                      ].map(item => (
                        <div key={item.label} className="text-center p-3 rounded-lg border bg-white">
                          <div className="text-[10px] text-slate-500 mb-1">{item.label}</div>
                          <div className="text-xs text-slate-600">{item.base}</div>
                          <ArrowRight className="w-3 h-3 mx-auto text-slate-300 my-1" />
                          <div className="text-xs font-bold text-[#0F2040]">{item.comp}</div>
                          <DeltaBadge value={item.delta} suffix={item.suffix} prefix={item.prefix} />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick navigation hint */}
              <div className="flex items-center justify-center gap-3 py-2">
                <button onClick={() => switchTab('timeline')} className="text-xs text-[#4ABFCF] hover:text-[#0F2040] transition-colors flex items-center gap-1" data-testid="goto-timeline">
                  <GanttIcon className="w-3.5 h-3.5" /> View Timeline →
                </button>
                <span className="text-slate-300">|</span>
                <button onClick={() => switchTab('resources')} className="text-xs text-[#4ABFCF] hover:text-[#0F2040] transition-colors flex items-center gap-1" data-testid="goto-resources">
                  <UserCog className="w-3.5 h-3.5" /> View Resources →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════════════ TAB: TIMELINE ════════════════ */}
      {activeTab === 'timeline' && timeline && (
        <div className="space-y-5" data-testid="timeline-tab">
          {hasConditions && (
            <div
              className="flex items-start gap-3 px-4 py-3 rounded-lg bg-gradient-to-r from-[#4ABFCF]/10 to-[#0F2040]/5 border border-[#4ABFCF]/30"
              data-testid="planner-conditions-banner"
            >
              <Lock className="w-4 h-4 text-[#0F2040] mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-xs font-bold text-[#0F2040] uppercase tracking-wider">
                  Read-only · Driven by Project Conditions
                </div>
                <p className="text-[12px] text-slate-700 mt-1">
                  This estimate's schedule is derived from the conditions you set:
                  {projectConditions?.goal_duration_weeks ? (
                    <span className="font-semibold"> {projectConditions.goal_duration_weeks}-week goal duration</span>
                  ) : null}
                  {projectConditions?.goal_go_live_date ? (
                    <span className="font-semibold">, target Go-Live {projectConditions.goal_go_live_date}</span>
                  ) : null}
                  {projectConditions?.holiday_region ? (
                    <span className="font-semibold">, holidays: {projectConditions.holiday_region}</span>
                  ) : null}
                  . To change the timeline, edit the conditions on the estimate (drag-edits are disabled here).
                </p>
                <div className="flex flex-wrap gap-2 mt-2 text-[10px]">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded">
                    <span className="w-2 h-2 rounded-sm" style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(245,158,11,0.6) 0px, rgba(245,158,11,0.6) 2px, transparent 2px, transparent 4px)' }} />
                    Breaks ({(projectConditions?.breaks || []).length})
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-50 text-rose-800 border border-rose-200 rounded">
                    <span className="w-2 h-2 rounded-sm bg-rose-400/60" />
                    Blackouts ({(projectConditions?.blackouts || []).length})
                  </span>
                </div>
              </div>
            </div>
          )}
          <Card data-testid="gantt-chart">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-1.5">
                Project Schedule — Gantt View
                {ganttView === 'phase' && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="text-slate-400 hover:text-[#4ABFCF] transition-colors p-0.5 rounded"
                        aria-label="About phase ordering"
                        data-testid="phase-order-info-trigger"
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      side="bottom"
                      align="start"
                      className="w-72 text-[11px] leading-relaxed text-slate-600 p-3"
                      data-testid="phase-order-info-popover"
                    >
                      <div className="font-semibold text-[#0F2040] text-xs mb-1.5 flex items-center gap-1.5">
                        <Lock className="w-3 h-3 text-[#4ABFCF]" />
                        Phase order is locked
                      </div>
                      <p className="mb-2">
                        Phases follow the canonical 2026 LMS Implementation framework — not whatever compute timing happens to produce. So Validation (UAT) always sits below Execution, even if its first activity starts earlier.
                      </p>
                      <ol className="list-decimal list-inside space-y-0.5 text-slate-500">
                        <li>Initiation</li>
                        <li>Design / Discovery</li>
                        <li>Execution</li>
                        <li>Validation (UAT)</li>
                        <li>Go Live</li>
                        <li>Transition — Hypercare</li>
                      </ol>
                    </PopoverContent>
                  </Popover>
                )}
              </CardTitle>
              <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5" data-testid="gantt-view-toggle">
                {(() => {
                  const wavesInUse = !!(timeline.wave_schedule && timeline.wave_schedule.length > 0);
                  return (
                    <button
                      className={`text-[10px] px-2.5 py-1 rounded transition-colors ${ganttView === 'phase' ? 'bg-white shadow-sm font-semibold text-[#0F2040]' : 'text-slate-500'} ${wavesInUse ? 'opacity-40 cursor-not-allowed' : ''}`}
                      onClick={() => setGanttView('phase')}
                      data-testid="gantt-view-phase"
                      title={wavesInUse ? 'Waves in use — each wave carries its own go-live & transition, so the overall phase timeline (which appends Transition after the final go-live) is not representative. Use the Deployment Waves view.' : undefined}
                      disabled={wavesInUse || !timeline.phase_schedule || timeline.phase_schedule.length === 0}>
                      Implementation Phase
                    </button>
                  );
                })()}
                <button
                  className={`text-[10px] px-2.5 py-1 rounded transition-colors ${ganttView === 'group' ? 'bg-white shadow-sm font-semibold text-[#0F2040]' : 'text-slate-500'}`}
                  onClick={() => setGanttView('group')}
                  data-testid="gantt-view-group">
                  Component Group
                </button>
                {timeline.wave_schedule && timeline.wave_schedule.length > 0 && (
                  <button
                    className={`text-[10px] px-2.5 py-1 rounded transition-colors ${ganttView === 'waves' ? 'bg-white shadow-sm font-semibold text-[#0F2040]' : 'text-slate-500'}`}
                    onClick={() => setGanttView('waves')}
                    data-testid="gantt-view-waves">
                    Deployment Waves
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!(goalStartDate || timeline.goal_start_date) && (
                <div
                  className="mb-3 flex items-start gap-2 px-3 py-2 rounded-md bg-cyan-50/70 border border-cyan-200/60 text-[11px] text-cyan-900"
                  data-testid="gantt-no-startdate-banner"
                >
                  <CalendarDays className="w-3.5 h-3.5 mt-0.5 text-cyan-600 shrink-0" />
                  <span>
                    Add a <strong>project start date</strong> in the Estimator (or switch the Overview tab's duration mode to <strong>Goal Dates</strong> and set a Goal Start Date) to unlock calendar months and a "Today" marker on the Gantt.
                  </span>
                </div>
              )}
              <ReleaseConflictCallout
                goLiveDate={(timeline.milestones || []).find(m => (m?.name || '').toLowerCase().startsWith('go live'))?.date}
                releases={publicCloudReleases}
                onShiftGoLive={handleShiftGoLive}
              />
              <GanttChart
                schedule={ganttView === 'waves' && timeline.wave_schedule && timeline.wave_schedule.length > 0
                  ? timeline.wave_schedule
                  : ganttView === 'phase' && timeline.phase_schedule && timeline.phase_schedule.length > 0
                    ? timeline.phase_schedule
                    : timeline.group_schedule}
                onWaveTune={ganttView === 'waves' && selectedId && user?.role !== 'user' ? async ({ componentName, waveId, offset_weeks, duration_weeks }) => {
                  try {
                    const cfgRes = await axios.get(`${API_URL}/api/planner/waves/${selectedId}`, { withCredentials: true });
                    const c = cfgRes.data || {};
                    const prevTiming = c.component_timing?.[componentName]?.[waveId] || null;
                    const ct = { ...(c.component_timing || {}) };
                    ct[componentName] = { ...(ct[componentName] || {}), [waveId]: { offset_weeks, duration_weeks } };
                    await axios.put(`${API_URL}/api/planner/waves/${selectedId}`, { ...c, component_timing: ct }, { withCredentials: true });
                    generateTimeline(selectedId, { silent: true });
                    toast.success(`${componentName}: starts +${offset_weeks} wks, runs ${duration_weeks} wks`, {
                      duration: 8000,
                      action: {
                        label: 'Undo',
                        onClick: async () => {
                          try {
                            const res2 = await axios.get(`${API_URL}/api/planner/waves/${selectedId}`, { withCredentials: true });
                            const c2 = res2.data || {};
                            const ct2 = { ...(c2.component_timing || {}) };
                            const perWave = { ...(ct2[componentName] || {}) };
                            if (prevTiming) perWave[waveId] = prevTiming;
                            else delete perWave[waveId];
                            if (Object.keys(perWave).length) ct2[componentName] = perWave;
                            else delete ct2[componentName];
                            await axios.put(`${API_URL}/api/planner/waves/${selectedId}`, { ...c2, component_timing: ct2 }, { withCredentials: true });
                            generateTimeline(selectedId, { silent: true });
                            toast.success(`${componentName}: drag undone`);
                          } catch {
                            toast.error('Could not undo the drag');
                          }
                        },
                      },
                    });
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Could not update component timing');
                  }
                } : undefined}
                onMilestoneMove={selectedId && user?.role !== 'user' && !compareTimeline ? async (name, week, prevWeek, wasOverridden) => {
                  const patchWeek = (w) => axios.patch(
                    `${API_URL}/api/estimates/${selectedId}/milestone-override`,
                    { name, week: w },
                    { withCredentials: true }
                  );
                  try {
                    await patchWeek(week);
                    generateTimeline(selectedId, { silent: true });
                    toast.success(week == null ? `${name}: reset to computed week` : `${name}: moved to W${week}`, {
                      duration: 8000,
                      action: {
                        label: 'Undo',
                        onClick: async () => {
                          try {
                            await patchWeek(week == null ? prevWeek : (wasOverridden ? prevWeek : null));
                            generateTimeline(selectedId, { silent: true });
                            toast.success(`${name}: move undone`);
                          } catch {
                            toast.error('Could not undo the milestone move');
                          }
                        },
                      },
                    });
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Could not move the milestone');
                  }
                } : undefined}
                onMilestoneNote={selectedId && user?.role !== 'user' && !compareTimeline ? async (name, note) => {
                  try {
                    await axios.patch(
                      `${API_URL}/api/estimates/${selectedId}/milestone-note`,
                      { name, note },
                      { withCredentials: true }
                    );
                    generateTimeline(selectedId, { silent: true });
                    toast.success(note ? `${name}: note saved` : `${name}: note removed`);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Could not save the milestone note');
                  }
                } : undefined}
                phaseSchedule={timeline.phase_schedule}
                totalWeeks={timeline.total_duration_weeks}
                compareSchedule={ganttView === 'phase' ? compareTimeline?.phase_schedule : compareTimeline?.group_schedule}
                planMode={timeline.plan_mode}
                milestones={timeline.milestones}
                estimateId={selectedId}
                startDate={goalStartDate || timeline.goal_start_date || timeline.project_start_date || null}
                defaultMilestonesCollapsed={timeline?.ui_defaults?.gantt_milestones_collapsed}
                defaultMilestonesCollapsedSource={timeline?.ui_defaults?.gantt_milestones_collapsed_source}
                releases={publicCloudReleases}
                editable={ganttView === 'group' && !!selectedId && !compareTimeline && !hasConditions}
                conditions={projectConditions}
                onBlocksChange={async (compName, blocks) => {
                  if (!selectedId) return;
                  try {
                    await axios.patch(
                      `${API_URL}/api/estimates/${selectedId}/component-blocks`,
                      { component_name: compName, blocks },
                      { withCredentials: true }
                    );
                    // Silent refetch — don't blank the timeline (would unmount
                    // GanttChart and lose the user's expand-all state).
                    const payload = {
                      estimate_id: selectedId,
                      resource_overrides: localFte,
                      hours_per_day: localHpd,
                      days_per_week: localDpw,
                      utilization_pct: localUtil,
                    };
                    if (durationMode === 'preset' || durationMode === 'manual') {
                      payload.plan_mode = 'duration';
                      if (projectWeeks) payload.project_weeks = projectWeeks;
                    } else if (durationMode === 'goals') {
                      payload.plan_mode = 'goals';
                      payload.goal_start_date = goalStartDate;
                      payload.goal_golive_date = goalGoLiveDate;
                    } else if (durationMode === 'absolute') {
                      payload.plan_mode = 'absolute';
                      payload.phase_dates = phaseDates;
                      if (projectWeeks) payload.project_weeks = projectWeeks;
                    }
                    const res = await axios.post(`${API_URL}/api/planner/timeline`, payload, { withCredentials: true });
                    setTimeline(res.data);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Could not update block schedule');
                  }
                }} />
            </CardContent>
          </Card>
          {timeline.milestones && timeline.milestones.length > 0 && (
            <MilestonesPanel milestones={timeline.milestones} planMode={timeline.plan_mode} />
          )}
        </div>
      )}

      {/* ════════════════ TAB: RESOURCES ════════════════ */}
      {activeTab === 'resources' && timeline && (
        <div className="space-y-5" data-testid="resources-tab">
          <RoleAllocationPanel timeline={timeline} roleLabels={roleLabels} />
          <WeeklyLoadingChart timeline={timeline} roleLabels={roleLabels} />
          {timeline.client_resources && timeline.client_resources.length > 0 && (
            <ClientResourcePanel timeline={timeline} />
          )}
          {selectedId && (
            <ResourceAssignmentsPanel estimateId={selectedId} />
          )}
        </div>
      )}

      {/* ════════════════ TAB: WAVES ════════════════ */}
      {activeTab === 'waves' && (
        <div className="space-y-5" data-testid="waves-tab">
          <SlipRollupCard
            currentEstimateId={selectedId}
            onOpenEstimate={(id) => { if (id !== selectedId) setSelectedId(id); }}
          />
          {selectedId && timeline ? (
            <WavesPanel
              currencySymbol={getCurrencySymbol(estimates.find(e => e.id === selectedId)?.currency_code)}
              estimateId={selectedId}
              timeline={timeline}
              onSaved={() => generateTimeline(selectedId)}
              readOnly={user?.role === 'user'}
            />
          ) : (
            <Card><CardContent className="py-10 text-center text-sm text-slate-400">
              Select an estimate to configure deployment waves.
            </CardContent></Card>
          )}
        </div>
      )}

      {/* ════════════════ TAB: WHAT-IF ════════════════ */}
      {activeTab === 'whatif' && (
        <div className="space-y-5" data-testid="whatif-tab">
          {selectedId ? (
            <>
              <SectionGuide id="what-if-simulator" title="What-If Scenario Simulator" accent="#8B5CF6" resetKey={guideResetKey}>
                <p>Toggle project factors, add or remove components, and override effort hours to see how changes ripple through the timeline. Save interesting scenarios as <strong>Snapshots</strong> for comparison and export.</p>
              </SectionGuide>
              <WhatIfSimulator
                estimateId={selectedId}
                estimate={estimates.find(e => e.id === selectedId)}
                onApplyTimeline={(tl) => setTimeline(tl)}
                onResultChange={(res) => setWhatIfResult(res)}
                onNarrativeChange={(n) => setWhatIfNarrative(n)}
              />

              <SectionGuide id="scenario-snapshots" title="Scenario Snapshots & Comparison" accent="#A855F7" resetKey={guideResetKey}>
                <p>Save What-If scenarios, compare side-by-side, and export branded comparison documents in PDF, Word, Excel, or PowerPoint.</p>
              </SectionGuide>
              <SnapshotManager
                currencySymbol={getCurrencySymbol(estimates.find(e => e.id === selectedId)?.currency_code)}
                estimateId={selectedId}
                estimateName={estimates.find(e => e.id === selectedId)?.estimate_name || ''}
                currentResult={whatIfResult}
                whatIfNarrative={whatIfNarrative}
              />
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <FlaskConical className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Select an estimate on the <button onClick={() => switchTab('overview')} className="text-[#4ABFCF] hover:underline">Overview</button> tab first.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      </div>

      <PlannerTemplateDialog
        open={templateBrowseOpen}
        onOpenChange={setTemplateBrowseOpen}
        mode="browse"
        onApply={handleApplyTemplate}
        isSuperAdmin={isSuperAdmin}
        currentSettings={currentTemplateSettings}
      />
      <PlannerTemplateDialog
        open={templateSaveOpen}
        onOpenChange={setTemplateSaveOpen}
        mode="save"
        onApply={handleApplyTemplate}
        isSuperAdmin={isSuperAdmin}
        currentSettings={currentTemplateSettings}
      />
      {/* Revision History side dialog — appears when user clicks the
          History icon on a saved plan. Shows full audit trail with
          who/when/what-changed and a one-click Restore per revision. */}
      <Dialog open={!!historyPlanId} onOpenChange={(open) => { if (!open) { setHistoryPlanId(null); setHistoryRevisions([]); setCompareA(null); setCompareB(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="plan-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
              <History className="w-4 h-4" /> Revision History — <span className="text-slate-500 font-normal truncate">{historyPlanName}</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              Every save is recorded as a revision. Restoring a revision creates a new revision (so a restore is itself reversible). Up to 20 most-recent revisions are kept per plan. Click <strong className="text-slate-500">A</strong>/<strong className="text-slate-500">B</strong> on any two rows to see a side-by-side diff.
            </DialogDescription>
          </DialogHeader>
          {/* Side-by-side diff panel — appears when both A and B slots
              are populated. Pre-seeded to (current vs prior) on open. */}
          {(() => {
            if (!compareA || !compareB || compareA === compareB) return null;
            const revA = historyRevisions.find(r => r.revision_number === compareA);
            const revB = historyRevisions.find(r => r.revision_number === compareB);
            if (!revA || !revB) return null;
            const sA = revA.summary || {};
            const sB = revB.summary || {};
            const fmtNum = (n, d = 1) => n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
            const fmtDelta = (a, b, d = 1, suffix = '') => {
              if (a == null || b == null) return null;
              const diff = Number(b) - Number(a);
              if (Math.abs(diff) < 0.01) return { text: 'no change', cls: 'text-slate-400' };
              const sign = diff > 0 ? '+' : '';
              return {
                text: `${sign}${fmtNum(diff, d)}${suffix}`,
                cls: diff > 0 ? 'text-amber-600' : 'text-emerald-600',
              };
            };
            const rows = [
              { label: 'Effort',   a: fmtNum(sA.total_effort_hours, 1) + ' hrs', b: fmtNum(sB.total_effort_hours, 1) + ' hrs', d: fmtDelta(sA.total_effort_hours, sB.total_effort_hours, 1, ' hrs') },
              { label: 'Cost',     a: sA.total_cost != null ? '$' + fmtNum(sA.total_cost, 0) : '—', b: sB.total_cost != null ? '$' + fmtNum(sB.total_cost, 0) : '—', d: fmtDelta(sA.total_cost, sB.total_cost, 0, '') && { ...fmtDelta(sA.total_cost, sB.total_cost, 0, ''), text: (Number(sB.total_cost || 0) - Number(sA.total_cost || 0) > 0 ? '+$' : '-$') + fmtNum(Math.abs(Number(sB.total_cost || 0) - Number(sA.total_cost || 0)), 0) } },
              { label: 'Duration', a: fmtNum(sA.total_duration_weeks, 0) + 'w', b: fmtNum(sB.total_duration_weeks, 0) + 'w', d: fmtDelta(sA.total_duration_weeks, sB.total_duration_weeks, 0, 'w') },
              { label: 'Peak FTE', a: fmtNum(sA.peak_fte, 2), b: fmtNum(sB.peak_fte, 2), d: fmtDelta(sA.peak_fte, sB.peak_fte, 2, '') },
            ];
            return (
              <div className="border border-slate-200 rounded-md p-3 bg-gradient-to-br from-slate-50 to-white" data-testid="revision-diff-panel">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Compare</span>
                  <span className="text-xs font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">v{revA.revision_number}</span>
                  <ArrowRight className="w-3 h-3 text-slate-400" />
                  <span className="text-xs font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">v{revB.revision_number}</span>
                  <span className="text-[10px] text-slate-400 truncate ml-1">{revA.note ? `"${revA.note}"` : 'no note'} → {revB.note ? `"${revB.note}"` : 'no note'}</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  {rows.map((row) => (
                    <div key={row.label} className="bg-white border border-slate-200 rounded p-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">{row.label}</p>
                      <p className="text-[11px] text-slate-500">
                        <span className="font-mono">{row.a}</span> <span className="text-slate-300">→</span> <span className="font-mono font-semibold text-slate-700">{row.b}</span>
                      </p>
                      {row.d && (
                        <p className={`text-[11px] font-semibold mt-0.5 ${row.d.cls}`}>{row.d.text}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
            {historyLoading && (
              <div className="flex items-center justify-center py-8 text-slate-400 text-xs">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading revisions…
              </div>
            )}
            {!historyLoading && historyRevisions.length === 0 && (
              <p className="text-center text-slate-400 text-xs py-8">No revisions on file yet.</p>
            )}
            {historyRevisions.map((r, idx) => {
              const isLatest = idx === 0;
              const summary = r.summary || {};
              const restoredFrom = r.restored_from_revision;
              const isA = compareA === r.revision_number;
              const isB = compareB === r.revision_number;
              return (
                <div
                  key={r.revision_number}
                  className={`border rounded-md p-3 ${isLatest ? 'border-blue-200 bg-blue-50/40' : 'border-slate-200 bg-white'}`}
                  data-testid={`revision-row-${r.revision_number}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-blue-700 bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded">v{r.revision_number}</span>
                        {isLatest && <span className="text-[9px] font-semibold text-blue-700">CURRENT</span>}
                        {restoredFrom && (
                          <span className="text-[9px] font-medium text-purple-700 bg-purple-100 border border-purple-200 px-1.5 py-0.5 rounded">
                            ↻ restored from v{restoredFrom}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-600">
                        <span className="font-medium text-[#0F2040]">{r.saved_by_name || r.saved_by_email || '—'}</span>
                        <span className="text-slate-400"> · {r.saved_at ? new Date(r.saved_at).toLocaleString() : '—'}</span>
                      </p>
                      {r.note && (
                        <p className="text-xs text-slate-500 italic mt-1 truncate" title={r.note}>"{r.note}"</p>
                      )}
                      <div className="flex flex-wrap gap-3 mt-1.5 text-[10px] text-slate-400">
                        {summary.total_effort_hours != null && <span>Effort: <span className="font-medium text-slate-600">{summary.total_effort_hours} hrs</span></span>}
                        {summary.total_cost != null && <span>Cost: <span className="font-medium text-slate-600">{getCurrencySymbol(estimates.find(e => e.id === (summary.estimate_id || selectedId))?.currency_code)}{Math.round(summary.total_cost).toLocaleString()}</span></span>}
                        {summary.total_duration_weeks != null && <span>Duration: <span className="font-medium text-slate-600">{summary.total_duration_weeks}w</span></span>}
                        {summary.peak_fte != null && <span>Peak FTE: <span className="font-medium text-slate-600">{summary.peak_fte}</span></span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {/* Compare toggle pair — A/B selectors. Clicking the
                          slot a row already occupies clears it (toggle). */}
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => setCompareA(isA ? null : r.revision_number)}
                          className={`text-[9px] font-bold rounded px-1.5 py-0.5 border transition-colors ${
                            isA ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-500 border-slate-300 hover:border-[#0F2040] hover:text-[#0F2040]'
                          }`}
                          title="Set as compare A (before)"
                          data-testid={`compare-a-${r.revision_number}`}
                        >A</button>
                        <button
                          type="button"
                          onClick={() => setCompareB(isB ? null : r.revision_number)}
                          className={`text-[9px] font-bold rounded px-1.5 py-0.5 border transition-colors ${
                            isB ? 'bg-[#4ABFCF] text-white border-[#4ABFCF]' : 'bg-white text-slate-500 border-slate-300 hover:border-[#4ABFCF] hover:text-[#4ABFCF]'
                          }`}
                          title="Set as compare B (after)"
                          data-testid={`compare-b-${r.revision_number}`}
                        >B</button>
                      </div>
                      {!isLatest && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={() => restoreRevision(historyPlanId, r.revision_number)}
                          disabled={restoringRev === r.revision_number}
                          data-testid={`restore-revision-${r.revision_number}`}
                        >
                          {restoringRev === r.revision_number ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <RotateCcw className="w-3 h-3 mr-1" />
                          )}
                          Restore
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <p className="text-[10px] text-slate-400">{historyRevisions.length} revision{historyRevisions.length === 1 ? '' : 's'} on file</p>
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={exportRevisionsCsv}
              disabled={!historyRevisions.length}
              data-testid="export-revisions-csv"
            >
              <Download className="w-3 h-3 mr-1.5" /> Export CSV
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
